// ─────────────────────────────────────────────────────────────────────────────
// App.js — Mistermind Abacus Portal
// Fully updated: exception handling, performance cache, super admin init,
// login locking, forgot password/ID, no self-registration, admin user controls
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, createContext, useContext, useCallback, useMemo } from "react";
import logoImg from './logo.png';
import * as XLSX from "xlsx";
import { auth, db } from "./firebase";
import { initializeApp, getApps } from "firebase/app";
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut, onAuthStateChanged, sendPasswordResetEmail, updatePassword,
  getAuth,
} from "firebase/auth";
import {
  collection, addDoc, getDocs, updateDoc, deleteDoc,
  doc, query, where, serverTimestamp, getDoc, setDoc,
} from "firebase/firestore";

// ── Secondary Firebase app for creating users without disrupting admin session ─
const _getSecondaryAuth = () => {
  try {
    const existing = getApps().find(a => a.name === "secondary");
    const app = existing || initializeApp(auth.app.options, "secondary");
    return getAuth(app);
  } catch { return null; }
};

// ── Inject shimmer animation once ─────────────────────────────────────────────
(() => {
  if (document.getElementById("mma-shimmer")) return;
  const s = document.createElement("style");
  s.id = "mma-shimmer";
  s.textContent = "@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}";
  document.head.appendChild(s);
})();

// ── Theme ─────────────────────────────────────────────────────────────────────
const T = {
  primary:"#FF6B35", primaryDark:"#E55A25", secondary:"#1A1A2E",
  accent:"#FFD700", teal:"#00BFA5", purple:"#7C3AED", rose:"#F43F5E",
  blue:"#3B82F6", bg:"#F8F7FF", card:"#FFFFFF", border:"#E8E4F3",
  text:"#1A1A2E", muted:"#6B7280", success:"#10B981", warning:"#F59E0B", danger:"#EF4444",
};

// ── Auth Context ──────────────────────────────────────────────────────────────
const AuthCtx = createContext(null);
const useAuth = () => useContext(AuthCtx);

// ── Default RBAC ──────────────────────────────────────────────────────────────
const DEFAULT_ROLES = {
  superAdmin:    { label:"Super Admin",        emoji:"🏢", color:T.primary, pages:["dashboard","students","attendance","fees","manageTeachers","franchise","media","reports","manageUsers","rbacEditor"] },
  franchiseAdmin:{ label:"Franchise Admin",    emoji:"🏫", color:T.blue,   pages:["dashboard","students","attendance","fees","manageTeachers","media","reports"] },
  teacher:       { label:"Teacher",            emoji:"👨‍🏫", color:T.purple, pages:["dashboard","attendance","myStudents"] },
  parent:        { label:"Child/Parent Login", emoji:"👨‍👩‍👧", color:T.teal,   pages:["myChild","fees","practice"] },
};

// ── Constants ─────────────────────────────────────────────────────────────────
const COURSES  = ["Abacus Level 1","Abacus Level 2","Abacus Level 3","Abacus Level 4","Abacus Level 5","Abacus Level 6","Abacus Level 7","Abacus Level 8","Abacus Level 9","Abacus Level 10","Vedic Maths Level 1","Vedic Maths Level 2","Vedic Maths Level 3"];
const BATCHES  = ["Morning A (7-8 AM)","Morning B (8-9 AM)","Afternoon A (2-3 PM)","Evening A (5-6 PM)","Evening B (6-7 PM)"];
const GRADES   = ["1st","2nd","3rd","4th","5th","6th","7th","8th"];
const FEE_ST   = ["Pending","Paid","Partial"];
const PAY_MODE = ["Cash","UPI","Bank Transfer","Cheque"];

const MAX_ATTEMPTS      = 5;
const SUPER_ADMIN_EMAIL = "gadriyekiran@gmail.com";
// ⚠️ Move to .env: REACT_APP_SA_PWD=Kiran@1990 before production
const SUPER_ADMIN_PWD   = process.env.REACT_APP_SA_PWD || "Kiran@1990";
const PAGE_SIZE         = 20;

// ── In-Memory Cache (60-second TTL) ──────────────────────────────────────────
const _cache = {};
const _cacheTs = {};
const CACHE_TTL = 60_000;

const cachedDocs = async (key, buildQuery) => {
  const now = Date.now();
  if (_cache[key] && (now - _cacheTs[key]) < CACHE_TTL) return _cache[key];
  const snap = await getDocs(buildQuery());
  const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  _cache[key] = data;
  _cacheTs[key] = now;
  return data;
};
const bust = (...keys) => keys.forEach(k => { delete _cache[k]; delete _cacheTs[k]; });
const clearAllCache = () => { Object.keys(_cache).forEach(k => { delete _cache[k]; delete _cacheTs[k]; }); };

// ── Friendly Error Messages ───────────────────────────────────────────────────
const getFriendlyError = (e) => {
  if (!e) return "An unexpected error occurred. Please try again.";
  const code = (e?.code || e?.message || "").toLowerCase();
  if (code.includes("network") || code.includes("unavailable")) return "Network error — check your internet connection.";
  if (code.includes("permission-denied"))  return "Access denied — you don't have permission for this action.";
  if (code.includes("not-found"))          return "Record not found.";
  if (code.includes("wrong-password") || code.includes("invalid-credential")) return "Incorrect password.";
  if (code.includes("user-not-found"))     return "No account found with this email address.";
  if (code.includes("email-already-in-use")) return "An account with this email already exists.";
  if (code.includes("too-many-requests"))  return "Too many attempts. Please wait and try again later.";
  if (code.includes("weak-password"))      return "Password must be at least 6 characters.";
  if (code.includes("user-disabled"))      return "This account has been disabled. Contact your Super Admin.";
  if (code.includes("invalid-email"))      return "Please enter a valid email address.";
  if (code.includes("quota-exceeded"))     return "Storage quota exceeded. Please contact support.";
  if (code.includes("storage") || code.includes("deadline-exceeded")) return "Request timed out. Please try again.";
  return e?.message?.replace("Firebase: ","").replace(/\(auth.*\)/,"").trim() || "Something went wrong. Please try again.";
};

// ── Login Attempt Tracking ────────────────────────────────────────────────────
const _aKey = email => email.toLowerCase().replace(/\./g,"_").replace(/@/g,"__at__");

const checkLockedOut = async (email) => {
  try {
    const d = await getDoc(doc(db, "loginAttempts", _aKey(email)));
    return d.exists() && d.data().locked === true;
  } catch { return false; }
};

// In-memory fallback for attempt tracking (used when Firestore write fails)
const _memAttempts = {};

const recordFailedAttempt = async (email) => {
  const key = _aKey(email);
  try {
    const d = await getDoc(doc(db, "loginAttempts", key));
    const prev = d.exists() ? (d.data().attempts || 0) : (_memAttempts[key] || 0);
    const attempts = prev + 1;
    _memAttempts[key] = attempts; // always update memory
    const locked = attempts >= MAX_ATTEMPTS;
    try {
      await setDoc(doc(db, "loginAttempts", key), {
        email, attempts, locked,
        lockedAt: locked ? serverTimestamp() : null,
        lastAttemptAt: serverTimestamp(),
      }, { merge: true });
    } catch { /* Firestore write failed — memory tracking still active */ }
    // If locked, also update the user's Firestore profile
    if (locked) {
      try {
        const uSnap = await getDocs(query(collection(db, "users"), where("email", "==", email.toLowerCase().trim())));
        if (!uSnap.empty) await updateDoc(doc(db, "users", uSnap.docs[0].id), { locked: true, lockedAt: serverTimestamp() });
      } catch {}
    }
    return { attempts, locked };
  } catch {
    // Full fallback — pure in-memory
    _memAttempts[key] = (_memAttempts[key] || 0) + 1;
    const attempts = _memAttempts[key];
    return { attempts, locked: attempts >= MAX_ATTEMPTS };
  }
};

const clearLoginAttempts = async (email) => {
  try {
    await setDoc(doc(db, "loginAttempts", _aKey(email)), { attempts: 0, locked: false }, { merge: true });
  } catch {}
};

// ── Simple Toast System ───────────────────────────────────────────────────────
let _toastFn = null;
const toast = {
  success: msg => _toastFn?.({ msg, type:"success", id:Date.now() }),
  error:   msg => _toastFn?.({ msg, type:"error",   id:Date.now() }),
  warn:    msg => _toastFn?.({ msg, type:"warn",    id:Date.now() }),
  info:    msg => _toastFn?.({ msg, type:"info",    id:Date.now() }),
};

const ToastProvider = () => {
  const [t, setT] = useState(null);
  useEffect(() => { _toastFn = setT; return () => { _toastFn = null; }; }, []);
  useEffect(() => {
    if (!t) return;
    const timer = setTimeout(() => setT(null), 4500);
    return () => clearTimeout(timer);
  }, [t]);
  if (!t) return null;
  const clr = { success:T.success, error:T.rose, warn:T.warning, info:T.blue };
  const ico  = { success:"✅", error:"❌", warn:"⚠️", info:"ℹ️" };
  return (
    <div style={{ position:"fixed", top:20, right:20, zIndex:99999, background:"white", borderRadius:12, padding:"12px 18px", boxShadow:"0 8px 30px rgba(0,0,0,0.2)", border:`2px solid ${clr[t.type]}`, maxWidth:360, display:"flex", alignItems:"center", gap:10, fontSize:13, fontWeight:600, color:T.text, fontFamily:"'Segoe UI',system-ui,sans-serif", animation:"fadeIn .2s ease" }}>
      <span style={{ fontSize:17 }}>{ico[t.type]}</span>
      <span style={{ flex:1, lineHeight:1.4 }}>{t.msg}</span>
      <button onClick={()=>setT(null)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted, fontSize:18, padding:0, lineHeight:1 }}>×</button>
    </div>
  );
};

// ── Skeleton Loaders ──────────────────────────────────────────────────────────
const Skeleton = ({ h=14, w="100%", r=6, mb=8 }) => (
  <div style={{ width:w, height:h, borderRadius:r, marginBottom:mb, background:"linear-gradient(90deg,#f0f0f0 25%,#e4e4e4 50%,#f0f0f0 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.2s infinite linear" }}/>
);
const TableSkeleton = ({ rows=5, cols=6 }) => (
  <div style={{ padding:"10px 0" }}>
    {Array(rows).fill(0).map((_,i) => (
      <div key={i} style={{ display:"grid", gridTemplateColumns:`repeat(${cols},1fr)`, gap:10, marginBottom:12 }}>
        {Array(cols).fill(0).map((_,j) => <Skeleton key={j} h={14} mb={0}/>)}
      </div>
    ))}
  </div>
);

// ── Logo Component ────────────────────────────────────────────────────────────
const Logo = ({ h=38 }) => (
  <img src={logoImg} alt="Mistermind Abacus" height={h}
    style={{ width:"100%", height:"100%", display:"block", objectFit:"cover", borderRadius:10 }}
  />
);

// ── Icon ──────────────────────────────────────────────────────────────────────
const Icon = ({ name, size=18, color="currentColor" }) => {
  const D = {
    dashboard:   <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>,
    students:    <><circle cx="9" cy="7" r="4"/><path d="M2 21v-2a7 7 0 0 1 14 0v2"/><path d="M19 8l2 2-4 4"/></>,
    attendance:  <><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></>,
    fees:        <><circle cx="12" cy="12" r="10"/><path d="M12 6v2m0 8v2m-4-6h2a2 2 0 0 0 0-4H8m0 4h3a2 2 0 0 1 0 4H8"/></>,
    teachers:    <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    franchise:   <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
    leads:       <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="23" y1="11" x2="17" y2="11"/><line x1="20" y1="8" x2="20" y2="14"/></>,
    reports:     <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
    manageUsers: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="23" y1="11" x2="17" y2="11"/><line x1="20" y1="8" x2="20" y2="14"/></>,
    myChild:     <><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></>,
    myStudents:  <><circle cx="9" cy="7" r="4"/><path d="M2 21v-2a7 7 0 0 1 14 0v2"/></>,
    rbacEditor:  <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    logout:      <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>,
    plus:        <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>,
    edit:        <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
    trash:       <><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></>,
    check:       <><polyline points="20 6 9 17 4 12"/></>,
    x:           <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
    search:      <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
    save:        <><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></>,
    upload:      <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></>,
    download:    <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>,
    dollar:      <><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>,
    user:        <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
    shield:      <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    info:        <><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></>,
    filter:      <><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></>,
    calendar:    <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
    bell:        <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></>,
    lock:        <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    unlock:      <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></>,
    mail:        <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
    key:         <><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display:"inline-block", verticalAlign:"middle", flexShrink:0 }}>
      {D[name] || null}
    </svg>
  );
};

// ── UI Primitives ─────────────────────────────────────────────────────────────
const Badge = ({ children, color=T.primary }) => (
  <span style={{ background:color+"22", color, fontSize:11, fontWeight:700, padding:"3px 9px", borderRadius:20, whiteSpace:"nowrap" }}>{children}</span>
);
const SBadge = ({ status }) => {
  const m = { Paid:T.success, Pending:T.danger, Partial:T.warning, Present:T.success, Absent:T.danger, Active:T.success, Inactive:T.rose, Locked:T.warning };
  return <Badge color={m[status] || T.muted}>{status}</Badge>;
};
const RBadge = ({ role, roles }) => {
  try {
    const r = roles?.[role] || DEFAULT_ROLES[role];
    return r ? <Badge color={r.color}>{r.emoji} {r.label}</Badge> : <Badge>{role}</Badge>;
  } catch { return <Badge>{role}</Badge>; }
};

const Modal = ({ open, onClose, title, children, wide }) => {
  if (!open) return null;
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}>
      <div style={{ background:"white", borderRadius:18, width:"100%", maxWidth:wide?780:510, maxHeight:"90vh", overflow:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ padding:"18px 22px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, background:"white", zIndex:1 }}>
          <div style={{ fontWeight:800, fontSize:16, color:T.text }}>{title}</div>
          <button onClick={onClose} style={{ background:T.bg, border:"none", borderRadius:9, padding:6, cursor:"pointer" }}><Icon name="x" size={15} color={T.muted}/></button>
        </div>
        <div style={{ padding:22 }}>{children}</div>
      </div>
    </div>
  );
};

const Inp = ({ label, type="text", value, onChange, placeholder, required, readOnly, autoComplete }) => (
  <div style={{ marginBottom:13 }}>
    <label style={{ display:"block", fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:0.5, marginBottom:5 }}>{label}{required && <span style={{color:T.rose}}> *</span>}</label>
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} required={required} readOnly={readOnly} autoComplete={autoComplete || (type==="password" ? "new-password" : "off")}
      style={{ width:"100%", padding:"9px 11px", border:`1px solid ${T.border}`, borderRadius:8, fontSize:13, color:T.text, outline:"none", boxSizing:"border-box", background:readOnly ? T.bg : "white" }}/>
  </div>
);
const Sel = ({ label, value, onChange, options, required }) => (
  <div style={{ marginBottom:13 }}>
    <label style={{ display:"block", fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:0.5, marginBottom:5 }}>{label}{required && <span style={{color:T.rose}}> *</span>}</label>
    <select value={value} onChange={onChange} required={required} style={{ width:"100%", padding:"9px 11px", border:`1px solid ${T.border}`, borderRadius:8, fontSize:13, color:T.text, background:"white", outline:"none" }}>
      <option value="">-- Select --</option>
      {options.map(o => typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);
const Btn = ({ children, onClick, color=T.primary, outline, sm, disabled, type="button" }) => (
  <button type={type} onClick={onClick} disabled={disabled}
    style={{ background:outline?"white":color, color:outline?color:"white", border:`2px solid ${color}`, borderRadius:8, padding:sm?"5px 12px":"9px 17px", fontWeight:700, fontSize:sm?11:13, cursor:disabled?"not-allowed":"pointer", display:"inline-flex", alignItems:"center", gap:5, opacity:disabled?0.6:1, transition:"all 0.15s", whiteSpace:"nowrap" }}>
    {children}
  </button>
);
const Card = ({ icon, label, value, color=T.primary, sub }) => (
  <div style={{ background:T.card, borderRadius:13, padding:"17px 19px", border:`1px solid ${T.border}`, boxShadow:"0 2px 10px rgba(0,0,0,0.05)", display:"flex", alignItems:"center", gap:13 }}>
    <div style={{ width:46, height:46, borderRadius:12, background:color+"18", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><Icon name={icon} size={21} color={color}/></div>
    <div>
      <div style={{ color:T.muted, fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:0.6, marginBottom:2 }}>{label}</div>
      <div style={{ fontSize:23, fontWeight:800, color:T.text, lineHeight:1.1 }}>{value}</div>
      {sub && <div style={{ color:T.muted, fontSize:11, marginTop:2 }}>{sub}</div>}
    </div>
  </div>
);
// ── Student Name Badge (shows student name in sidebar for parent login) ────────
const StudentNameBadge = ({ email }) => {
  const [name, setName] = useState("Loading...");
  useEffect(() => {
    if (!email) return;
    getDocs(query(collection(db,"students"), where("parentEmail","==",email.toLowerCase())))
      .then(snap => { if (!snap.empty) setName(snap.docs[0].data().name || "Student"); })
      .catch(() => setName("Student"));
  }, [email]);
  return <span>{name}</span>;
};

const Denied = () => (
  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:60, textAlign:"center" }}>
    <div style={{ fontSize:48, marginBottom:14 }}>🔒</div>
    <div style={{ fontSize:20, fontWeight:800, color:T.text, marginBottom:6 }}>Access Restricted</div>
    <div style={{ color:T.muted, fontSize:13 }}>You don't have permission to view this page. Contact your Super Admin.</div>
  </div>
);

// ── COMING SOON PLACEHOLDER ───────────────────────────────────────────────────
const ComingSoon = ({ title, icon="🚧", description }) => (
  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:60, textAlign:"center", minHeight:360 }}>
    <div style={{ fontSize:64, marginBottom:16 }}>{icon}</div>
    <div style={{ fontSize:22, fontWeight:900, color:T.text, marginBottom:8 }}>{title}</div>
    <div style={{ background:`linear-gradient(135deg,${T.primary}15,${T.accent}15)`, border:`2px dashed ${T.primary}50`, borderRadius:14, padding:"14px 28px", marginBottom:18, display:"inline-block" }}>
      <span style={{ color:T.primary, fontWeight:700, fontSize:13 }}>🔜 Coming in Next Phase</span>
    </div>
    {description && <div style={{ color:T.muted, fontSize:13, maxWidth:400, lineHeight:1.7 }}>{description}</div>}
  </div>
);

// ── FORGOT PASSWORD / USER ID MODAL ───────────────────────────────────────────
const ForgotModal = ({ open, onClose }) => {
  const [tab, setTab]       = useState("password");
  const [email, setEmail]   = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setEmail(""); setResult(null); };

  const handleForgotPassword = async () => {
    if (!email.trim()) { setResult({ type:"error", msg:"Please enter your registered email address." }); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { setResult({ type:"error", msg:"Please enter a valid email address." }); return; }
    setLoading(true); setResult(null);
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setResult({ type:"success", msg:`Password reset link sent to ${email.trim()}. Check your inbox and spam/junk folder. Click the link in the email to set a new password.` });
      setEmail("");
    } catch (e) {
      setResult({ type:"error", msg:getFriendlyError(e) });
    }
    setLoading(false);
  };

  const handleForgotUserId = async () => {
    if (!email.trim()) { setResult({ type:"error", msg:"Please enter your registered email address." }); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { setResult({ type:"error", msg:"Please enter a valid email address." }); return; }
    setLoading(true); setResult(null);
    try {
      const snap = await getDocs(query(collection(db, "users"), where("email", "==", email.trim().toLowerCase())));
      if (snap.empty) {
        setResult({ type:"error", msg:"No account found with this email address. Please contact your Super Admin to check your registration." });
      } else {
        const u = snap.docs[0].data();
        setResult({ type:"success", msg:`Account found! Your User ID (login email) is: ${u.email}. Registered name: ${u.name || "—"}. Role: ${DEFAULT_ROLES[u.role]?.label || u.role}. Use the "Reset Password" tab if you also need to reset your password.` });
      }
    } catch (e) {
      setResult({ type:"error", msg:getFriendlyError(e) });
    }
    setLoading(false);
  };

  if (!open) return null;
  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title="🔑 Account Recovery">
      <div style={{ display:"flex", gap:0, marginBottom:20, border:`1px solid ${T.border}`, borderRadius:10, overflow:"hidden" }}>
        {[["password","🔐 Reset Password"],["userid","📧 Find User ID"]].map(([k,l]) => (
          <button key={k} onClick={() => { setTab(k); reset(); }}
            style={{ flex:1, padding:"10px 14px", background:tab===k?T.primary:"white", color:tab===k?"white":T.text, border:"none", fontWeight:700, fontSize:12, cursor:"pointer", transition:"all 0.15s" }}>
            {l}
          </button>
        ))}
      </div>

      {tab === "password" && (
        <>
          <p style={{ color:T.muted, fontSize:13, marginBottom:16, lineHeight:1.6 }}>
            Enter your registered email address. We'll send a secure reset link to that inbox.
          </p>
          <Inp label="Registered Email Address" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required/>
        </>
      )}
      {tab === "userid" && (
        <>
          <p style={{ color:T.muted, fontSize:13, marginBottom:16, lineHeight:1.6 }}>
            Enter the email address you registered with to look up your User ID and account details.
          </p>
          <Inp label="Your Registered Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required/>
        </>
      )}

      {result && (
        <div style={{ background:result.type==="success"?T.success+"15":T.rose+"15", border:`1px solid ${result.type==="success"?T.success:T.rose}40`, borderRadius:9, padding:"11px 14px", fontSize:12, color:result.type==="success"?T.success:T.rose, fontWeight:600, marginBottom:14, lineHeight:1.6 }}>
          {result.type==="success"?"✅ ":"⚠️ "}{result.msg}
        </div>
      )}

      <div style={{ display:"flex", gap:9, marginTop:10 }}>
        <Btn onClick={() => { reset(); onClose(); }} color={T.muted} outline>Cancel</Btn>
        <Btn onClick={tab==="password" ? handleForgotPassword : handleForgotUserId} disabled={loading} color={tab==="password" ? T.primary : T.blue}>
          <Icon name={tab==="password" ? "mail" : "search"} size={14} color="white"/>
          {loading ? "Please wait..." : tab==="password" ? "Send Reset Link" : "Find My User ID"}
        </Btn>
      </div>
    </Modal>
  );
};

// ── LOGIN PAGE ────────────────────────────────────────────────────────────────
const LoginPage = ({ loginError, onClearError }) => {
  const [email, setEmail]     = useState("");
  const [pw, setPw]           = useState("");
  const [err, setErr]         = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  // Show any app-level login error (e.g. deactivated account)
  const displayErr = err || loginError;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); onClearError?.();
    if (!email.trim() || !pw.trim()) { setErr("Please enter your email and password."); return; }
    setLoading(true);
    try {
      // 1. Check lockout status
      const locked = await checkLockedOut(email.trim().toLowerCase());
      if (locked) {
        setErr(`This account is locked. Contact your Super Admin or use Forgot Password to unlock.`);
        setLoading(false);
        return;
      }
      // 2. Attempt sign-in
      await signInWithEmailAndPassword(auth, email.trim(), pw);
      // 3. Clear failed attempts on success
      await clearLoginAttempts(email.trim().toLowerCase());
    } catch (e) {
      const code = e?.code || "";
      if (code.includes("wrong-password") || code.includes("invalid-credential") || code.includes("invalid-password") || code.includes("user-not-found") || code.includes("invalid-email")) {
        // Record failed attempt for any bad credential
        const { attempts, locked } = await recordFailedAttempt(email.trim().toLowerCase());
        if (locked) {
          setErr(`Account locked. Contact your Super Admin or use Forgot Password to unlock.`);
        } else {
          const remaining = MAX_ATTEMPTS - attempts;
          setErr(`Incorrect User ID or Password. ${remaining} attempt${remaining !== 1 ? "s" : ""} remaining before account lockout.`);
        }
      } else {
        setErr(getFriendlyError(e));
      }
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", fontFamily:"'Segoe UI',system-ui,sans-serif", overflow:"hidden" }}>

      {/* ── LEFT — Branding ── */}
      <div style={{ flex:1, background:"linear-gradient(160deg,#1A1A2E 0%,#0F3460 50%,#1a1a2e 100%)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 32px", position:"relative", overflow:"hidden", minWidth:0 }}>
        <div style={{ position:"absolute", top:-80, left:-80, width:280, height:280, borderRadius:"50%", background:"rgba(255,107,53,0.08)", pointerEvents:"none" }}/>
        <div style={{ position:"absolute", bottom:-60, right:-60, width:220, height:220, borderRadius:"50%", background:"rgba(255,215,0,0.06)", pointerEvents:"none" }}/>
        <div style={{ position:"absolute", top:"40%", right:-40, width:160, height:160, borderRadius:"50%", background:"rgba(0,191,165,0.07)", pointerEvents:"none" }}/>

        <div style={{ background:"white", borderRadius:16, padding:"10px 24px", marginBottom:32, boxShadow:"0 8px 32px rgba(0,0,0,0.25)", width:260, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <img src={logoImg} alt="Mistermind Abacus" style={{ width:"100%", height:"auto", display:"block", borderRadius:10 }}/>
        </div>
        <div style={{ color:"rgba(255,255,255,0.9)", fontSize:22, fontWeight:800, textAlign:"center", marginBottom:8, letterSpacing:-0.3 }}>Unleash The Genius In Every Child!</div>
        <div style={{ color:"rgba(255,255,255,0.5)", fontSize:13, textAlign:"center", marginBottom:40, lineHeight:1.6, maxWidth:340 }}>India's leading abacus & vedic maths franchise management platform</div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, width:"100%", maxWidth:360, marginBottom:32 }}>
          {[
            { emoji:"🧮", num:"4,820+", label:"Students Enrolled",   bg:"rgba(255,107,53,0.15)", border:"rgba(255,107,53,0.3)" },
            { emoji:"🏫", num:"28",     label:"Franchise Centers",   bg:"rgba(255,215,0,0.12)",  border:"rgba(255,215,0,0.3)"  },
            { emoji:"👨‍🏫", num:"150+",  label:"Expert Teachers",     bg:"rgba(0,191,165,0.12)",  border:"rgba(0,191,165,0.3)"  },
            { emoji:"🏆", num:"1,284+", label:"Certificates Issued", bg:"rgba(124,58,237,0.12)", border:"rgba(124,58,237,0.3)" },
          ].map(s => (
            <div key={s.label} style={{ background:s.bg, border:`1px solid ${s.border}`, borderRadius:12, padding:"14px 12px", textAlign:"center" }}>
              <div style={{ fontSize:24, marginBottom:4 }}>{s.emoji}</div>
              <div style={{ color:"white", fontWeight:900, fontSize:18, lineHeight:1 }}>{s.num}</div>
              <div style={{ color:"rgba(255,255,255,0.55)", fontSize:10, fontWeight:600, marginTop:3, textTransform:"uppercase", letterSpacing:0.4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display:"flex", gap:10, flexWrap:"wrap", justifyContent:"center" }}>
          {["➕ Addition","✖️ Multiplication","➗ Division","🔢 Vedic Maths","🧠 Mental Math"].map(t => (
            <span key={t} style={{ background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:20, padding:"5px 12px", color:"rgba(255,255,255,0.6)", fontSize:11, fontWeight:600 }}>{t}</span>
          ))}
        </div>
        <div style={{ position:"absolute", bottom:20, color:"rgba(255,255,255,0.25)", fontSize:11 }}>mma.mistermindabacus.in</div>
      </div>

      {/* ── RIGHT — Login Form ── */}
      <div style={{ width:440, background:"#F8F7FF", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 36px", flexShrink:0 }}>
        <div style={{ width:"100%", marginBottom:28 }}>
          <div style={{ fontSize:26, fontWeight:900, color:"#1A1A2E", marginBottom:4 }}>Welcome Back! 👋</div>
          <div style={{ fontSize:13, color:"#6B7280" }}>Sign in to your Mistermind portal</div>
        </div>

        <form onSubmit={submit} style={{ width:"100%" }}>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.6, marginBottom:6 }}>Email Address (User ID)</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required
              autoComplete="username"
              style={{ width:"100%", padding:"11px 14px", background:"white", border:"1.5px solid #E8E4F3", borderRadius:10, fontSize:13, color:"#1A1A2E", outline:"none", boxSizing:"border-box" }}
              onFocus={e => e.target.style.borderColor="#FF6B35"}
              onBlur={e => e.target.style.borderColor="#E8E4F3"}/>
          </div>

          <div style={{ marginBottom:6 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#6B7280", textTransform:"uppercase", letterSpacing:0.6, marginBottom:6 }}>Password</label>
            <input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Your password" required
              autoComplete="current-password"
              style={{ width:"100%", padding:"11px 14px", background:"white", border:"1.5px solid #E8E4F3", borderRadius:10, fontSize:13, color:"#1A1A2E", outline:"none", boxSizing:"border-box" }}
              onFocus={e => e.target.style.borderColor="#FF6B35"}
              onBlur={e => e.target.style.borderColor="#E8E4F3"}/>
          </div>

          {/* Forgot link */}
          <div style={{ textAlign:"right", marginBottom:16 }}>
            <button type="button" onClick={() => setShowForgot(true)}
              style={{ background:"none", border:"none", color:T.primary, fontSize:12, fontWeight:700, cursor:"pointer", padding:0 }}>
              Forgot User ID / Password?
            </button>
          </div>

          {displayErr && (
            <div style={{ background:"#FFF0F3", border:"1px solid #F43F5E40", borderRadius:9, padding:"10px 13px", color:"#F43F5E", fontSize:12, marginBottom:14, fontWeight:600, lineHeight:1.5 }}>
              ⚠️ {displayErr}
            </div>
          )}

          <button type="submit" disabled={loading}
            style={{ width:"100%", padding:"13px", background:"linear-gradient(135deg,#FF6B35,#E55A25)", border:"none", borderRadius:11, color:"white", fontWeight:800, fontSize:15, cursor:loading?"not-allowed":"pointer", opacity:loading?0.75:1, marginBottom:16, boxShadow:"0 4px 14px rgba(255,107,53,0.35)", transition:"all 0.2s" }}>
            {loading ? "Signing in..." : "🔐 Sign In"}
          </button>
        </form>

        {/* Role guide */}
        <div style={{ width:"100%", background:"white", borderRadius:12, padding:"14px 16px", border:"1px solid #E8E4F3" }}>
          <div style={{ fontSize:10, fontWeight:800, color:"#9CA3AF", textTransform:"uppercase", letterSpacing:0.8, marginBottom:10 }}>Portal Access Roles</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
            {Object.entries(DEFAULT_ROLES).map(([k,v]) => (
              <div key={k} style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 8px", background:"#F8F7FF", borderRadius:7 }}>
                <span style={{ fontSize:14 }}>{v.emoji}</span>
                <span style={{ fontSize:11, color:"#374151", fontWeight:600 }}>{v.label}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop:10, fontSize:11, color:T.muted, lineHeight:1.5 }}>
            🔒 Contact your Super Admin to get your login credentials.
          </div>
        </div>

        <div style={{ marginTop:20, fontSize:11, color:"#9CA3AF", textAlign:"center" }}>
          © 2025 Mistermind Abacus · mma.mistermindabacus.in
        </div>
      </div>

      <ForgotModal open={showForgot} onClose={() => setShowForgot(false)}/>
    </div>
  );
};

// ── BULK UPLOAD MODAL ─────────────────────────────────────────────────────────
const BulkUploadModal = ({ open, onClose, userProfile, onSuccess }) => {
  const [file, setFile]         = useState(null);
  const [preview, setPreview]   = useState([]);
  const [errors, setErrors]     = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(0);

  const HDRS = ["Student Name","Age","Grade","Parent Name","Parent Email","Phone","Course","Batch","Fee Status","Center"];

  const downloadTemplate = () => {
    try {
      // Dynamically load SheetJS
      const wb = XLSX.utils.book_new();

      // ── Main data sheet ──────────────────────────────────────────────────
      const sampleData = [
        HDRS,
        ["Aarav Sharma","9","4th","Rahul Sharma","rahul@email.com","9876543210","Abacus Level 1","Morning A (7-8 AM)","Pending","Powai Center"],
        ["Priya Patel","10","5th","Meena Patel","meena@email.com","9823456780","Abacus Level 2","Evening B (6-7 PM)","Pending","Andheri Center"],
      ];
      const ws = XLSX.utils.aoa_to_sheet(sampleData);

      // Column widths
      ws["!cols"] = [
        {wch:20},{wch:6},{wch:8},{wch:20},{wch:28},{wch:14},
        {wch:22},{wch:22},{wch:12},{wch:18}
      ];

      // ── Validation lists sheet (hidden) ──────────────────────────────────
      const courses  = COURSES;
      const batches  = BATCHES;
      const feeStats = FEE_ST;
      const grades   = GRADES;

      const maxRows = Math.max(courses.length, batches.length, feeStats.length, grades.length);
      const listData = [["Course","Batch","Fee Status","Grade"]];
      for (let i = 0; i < maxRows; i++) {
        listData.push([
          courses[i]||"", batches[i]||"", feeStats[i]||"", grades[i]||""
        ]);
      }
      const wsLists = XLSX.utils.aoa_to_sheet(listData);
      XLSX.utils.book_append_sheet(wb, wsLists, "Lists");

      // ── Data validation via !dataValidations ─────────────────────────────
      // Course column = G (index 6), rows 2–200
      // Batch column  = H (index 7), rows 2–200
      // Fee Status    = I (index 8), rows 2–200
      // Grade         = D (index 3), rows 2–200 (actually col 3 is Grade, 0-based)
      // HDRS: Student Name(A),Age(B),Grade(C),Parent Name(D),Parent Email(E),Phone(F),Course(G),Batch(H),Fee Status(I),Center(J)

      ws["!dataValidations"] = [
        {
          sqref: "G2:G200",
          type:  "list",
          formula1: `Lists!$A$2:$A${courses.length+1}`,
          showDropDown: false,
          showErrorMessage: true,
          errorTitle: "Invalid Course",
          error: "Please select a valid course from the dropdown list.",
          errorStyle: "stop",
        },
        {
          sqref: "H2:H200",
          type:  "list",
          formula1: `Lists!$B$2:$B${batches.length+1}`,
          showDropDown: false,
          showErrorMessage: true,
          errorTitle: "Invalid Batch",
          error: "Please select a valid batch time from the dropdown list.",
          errorStyle: "stop",
        },
        {
          sqref: "I2:I200",
          type:  "list",
          formula1: `Lists!$C$2:$C${feeStats.length+1}`,
          showDropDown: false,
          showErrorMessage: true,
          errorTitle: "Invalid Fee Status",
          error: "Fee Status must be: Pending, Paid, or Partial.",
          errorStyle: "stop",
        },
        {
          sqref: "C2:C200",
          type:  "list",
          formula1: `Lists!$D$2:$D${grades.length+1}`,
          showDropDown: false,
          showErrorMessage: true,
          errorTitle: "Invalid Grade",
          error: "Please select a valid grade from the dropdown list.",
          errorStyle: "warning",
        },
      ];

      // Style header row bold + orange fill
      const headerStyle = { font:{ bold:true, color:{rgb:"FFFFFF"} }, fill:{ fgColor:{rgb:"FF6B35"} }, alignment:{ horizontal:"center" } };
      HDRS.forEach((_, ci) => {
        const cellRef = XLSX.utils.encode_cell({ r:0, c:ci });
        if (!ws[cellRef]) ws[cellRef] = {};
        ws[cellRef].s = headerStyle;
      });

      // Phone column (F) — number format
      ws["!cols"][5] = { wch:14, z:"0" };

      XLSX.utils.book_append_sheet(wb, ws, "Students");
      // Move Students sheet to first position
      wb.SheetNames = ["Students", "Lists"];

      XLSX.writeFile(wb, "mistermind_bulk_upload_template.xlsx");
      toast.success("Template downloaded! Open in Excel — Course, Batch, Fee Status columns have dropdowns.");
    } catch(err) {
      toast.error("Could not generate template: " + err.message);
    }
  };

  const parseCSV = (text) => {
    const lines = text.replace(/\r\n/g,"\n").replace(/\r/g,"\n").split("\n").filter(l => l.trim());
    if (lines.length < 2) throw new Error("File is empty or has no data rows");
    const parseRow = line => {
      const vals = []; let cur = "", inQ = false;
      for (const ch of line+",") {
        if (ch === '"') inQ = !inQ;
        else if (ch === "," && !inQ) { vals.push(cur.replace(/^"|"$/g,"").trim()); cur = ""; }
        else cur += ch;
      }
      return vals;
    };
    const headers = parseRow(lines[0]);
    return lines.slice(1).map(l => parseRow(l)).filter(r => r.some(v => v))
      .map(vals => headers.reduce((obj,h,i) => ({...obj,[h]:vals[i]||""}),{}));
  };

  const validate = (row, idx) => {
    const e = [];
    if (!row["Student Name"]?.trim()) e.push(`Row ${idx+2}: Student Name is required`);
    if (!row["Course"] || !COURSES.includes(row["Course"])) e.push(`Row ${idx+2}: Invalid Course`);
    if (!row["Batch"] || !BATCHES.includes(row["Batch"])) e.push(`Row ${idx+2}: Invalid Batch`);
    if (row["Phone"] && !/^\d{10}$/.test(row["Phone"])) e.push(`Row ${idx+2}: Phone must be 10 digits`);
    if (row["Age"] && (isNaN(row["Age"]) || !Number.isInteger(+row["Age"]) || +row["Age"]<3 || +row["Age"]>18)) e.push(`Row ${idx+2}: Age must be a whole number between 3 and 18`);
    if (row["Fee Status"] && !FEE_ST.includes(row["Fee Status"])) e.push(`Row ${idx+2}: Fee Status must be Paid, Pending or Partial`);
    return e;
  };

  const handleFile = async e => {
    const f = e.target.files?.[0];
    if (!f) return;
    const name = f.name.toLowerCase();
    const isXlsx = name.endsWith(".xlsx") || name.endsWith(".xls");
    const isCsv  = name.endsWith(".csv");
    if (!isXlsx && !isCsv) {
      toast.error("Please upload the .xlsx template file (or a .csv file).");
      return;
    }
    setFile(f); setErrors([]); setPreview([]);

    if (isXlsx) {
      // Parse XLSX using SheetJS
      try {
        const arrayBuf = await f.arrayBuffer();
        const wb = XLSX.read(arrayBuf, { type:"array" });
        const ws = wb.Sheets["Students"] || wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval:"" });
        // Normalise keys — trim whitespace from header names
        const rows = raw.map(r => {
          const obj = {};
          Object.entries(r).forEach(([k,v]) => { obj[k.trim()] = String(v).trim(); });
          return obj;
        });
        setErrors(rows.flatMap((r,i) => validate(r,i)));
        setPreview(rows);
      } catch(err) {
        setErrors(["Could not read Excel file: " + err.message]);
      }
    } else {
      // Parse CSV (legacy support)
      const reader = new FileReader();
      reader.onload = ev => {
        try {
          const rows = parseCSV(ev.target.result);
          setErrors(rows.flatMap((r,i) => validate(r,i)));
          setPreview(rows);
        } catch (err) { setErrors([err.message]); }
      };
      reader.onerror = () => { toast.error("Could not read file. Please try again."); };
      reader.readAsText(f, "UTF-8");
    }
  };

  // ── Parallel upload with concurrency of 10 ────────────────────────────────
  const handleUpload = async () => {
    if (errors.length > 0) { toast.error("Fix all errors before uploading"); return; }
    if (preview.length === 0) { toast.error("No rows to upload"); return; }
    setUploading(true); setUploaded(0);
    const CONCURRENT = 10;
    try {
      for (let i = 0; i < preview.length; i += CONCURRENT) {
        const batch = preview.slice(i, i + CONCURRENT);
        await Promise.all(batch.map((row, j) =>
          addDoc(collection(db, "students"), {
            name:        row["Student Name"]?.trim() || "",
            age:         row["Age"]?.trim() || "",
            grade:       row["Grade"]?.trim() || "",
            parentName:  row["Parent Name"]?.trim() || "",
            parentEmail: row["Parent Email"]?.trim().toLowerCase() || "",
            phone:       row["Phone"]?.trim() || "",
            course:      row["Course"]?.trim() || "",
            batch:       row["Batch"]?.trim() || "",
            fee:         row["Fee Status"]?.trim() || "Pending",
            center:      row["Center"]?.trim() || userProfile?.center || "",
            teacherEmail:"",
            studentId:   "AVM-" + Date.now() + "-" + (i+j),
            attendance:  0,
            uploadedBy:  userProfile?.email,
            createdAt:   serverTimestamp(),
          })
        ));
        setUploaded(Math.min(i + CONCURRENT, preview.length));
      }
      bust("students-sa","students-fa-"+userProfile?.center,"students-t-"+userProfile?.email);
      toast.success(`${preview.length} students uploaded successfully!`);

      // Create parent login accounts for rows that have parentEmail
      const parentRows = preview.filter(r => r["Parent Email"]?.trim());
      if (parentRows.length > 0) {
        toast.info?.(`Creating ${parentRows.length} parent login account(s)…`);
        let created = 0;
        for (const row of parentRows) {
          const pEmail = row["Parent Email"].trim().toLowerCase();
          try {
            const existing = await getDocs(query(collection(db,"users"), where("email","==",pEmail)));
            if (!existing.empty) continue;
            const secAuth = _getSecondaryAuth();
            if (!secAuth) break;
            const pCred = await createUserWithEmailAndPassword(secAuth, pEmail, "mistermind");
            await addDoc(collection(db,"users"), {
              uid: pCred.user.uid, email: pEmail,
              name: row["Parent Name"]?.trim() || "Parent/Guardian",
              role: "parent", center: row["Center"]?.trim() || userProfile?.center || "",
              active: true, locked: false, firstLogin: true,
              createdAt: serverTimestamp(), createdBy: userProfile?.email,
            });
            await signOut(secAuth);
            created++;
          } catch(pe) {
            if (pe.code !== "auth/email-already-in-use") {
              console.warn("Parent account skip:", pEmail, pe.code);
            }
          }
        }
        if (created > 0) toast.success(`${created} parent login(s) created — default password: mistermind`);
      }

      onSuccess(); reset(); onClose();
    } catch (err) {
      toast.error("Upload error: " + getFriendlyError(err));
    }
    setUploading(false);
  };

  const reset = () => { setFile(null); setPreview([]); setErrors([]); setUploaded(0); };

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title="📥 Bulk Student Upload" wide>
      <div style={{ background:T.blue+"10", border:`1px solid ${T.blue}30`, borderRadius:11, padding:15, marginBottom:18 }}>
        <div style={{ fontWeight:800, color:T.blue, fontSize:14, marginBottom:5 }}>Step 1 — Download Excel Template</div>
        <div style={{ color:T.muted, fontSize:12, marginBottom:10, lineHeight:1.6 }}>
          Download the Excel template → Fill in student details → Upload below.<br/>
          <strong>Course, Batch and Fee Status columns have dropdown validation — select values from the list only.</strong>
        </div>
        <Btn onClick={downloadTemplate} color={T.blue} outline sm><Icon name="download" size={13} color={T.blue}/> Download Template (.xlsx)</Btn>
      </div>

      <div style={{ background:T.bg, border:`2px dashed ${T.border}`, borderRadius:11, padding:20, marginBottom:18, textAlign:"center" }}>
        <div style={{ fontWeight:800, color:T.text, fontSize:14, marginBottom:5 }}>Step 2 — Upload Filled Excel File</div>
        <Icon name="upload" size={30} color={T.muted}/>
        <div style={{ color:T.muted, fontSize:12, margin:"7px 0 11px" }}>Select your completed .xlsx (or .csv) file</div>
        <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} style={{ fontSize:13 }}/>
        {file && <div style={{ marginTop:10, color:T.success, fontSize:13, fontWeight:700 }}>✓ {file.name} — {preview.length} rows detected</div>}
      </div>

      {errors.length > 0 && (
        <div style={{ background:T.rose+"10", border:`1px solid ${T.rose}40`, borderRadius:9, padding:13, marginBottom:15, maxHeight:150, overflow:"auto" }}>
          <div style={{ fontWeight:800, color:T.rose, marginBottom:6, fontSize:13 }}>⚠️ {errors.length} error{errors.length > 1 ? "s" : ""} — fix and re-upload:</div>
          {errors.map((e,i) => <div key={i} style={{ fontSize:12, color:T.rose, padding:"2px 0" }}>• {e}</div>)}
        </div>
      )}

      {preview.length > 0 && errors.length === 0 && (
        <div style={{ marginBottom:18 }}>
          <div style={{ fontWeight:800, color:T.success, fontSize:13, marginBottom:9 }}>✅ {preview.length} students ready — preview:</div>
          <div style={{ overflowX:"auto", maxHeight:200, border:`1px solid ${T.border}`, borderRadius:9 }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
              <thead style={{ background:T.bg, position:"sticky", top:0 }}>
                <tr>{HDRS.map(h => <th key={h} style={{ padding:"7px 9px", textAlign:"left", color:T.muted, fontWeight:700, borderBottom:`1px solid ${T.border}`, whiteSpace:"nowrap" }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {preview.map((r,i) => (
                  <tr key={i} style={{ borderBottom:`1px solid ${T.border}` }}>
                    {HDRS.map(h => <td key={h} style={{ padding:"6px 9px", color:T.text, whiteSpace:"nowrap" }}>{r[h]||"—"}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {uploading && (
        <div style={{ background:T.success+"10", borderRadius:9, padding:13, marginBottom:14, textAlign:"center" }}>
          <div style={{ fontWeight:700, color:T.success, marginBottom:6 }}>Uploading {uploaded} of {preview.length} students...</div>
          <div style={{ background:T.border, borderRadius:10, height:8, overflow:"hidden" }}>
            <div style={{ width:`${(uploaded/preview.length)*100}%`, height:"100%", background:T.success, transition:"width 0.3s" }}/>
          </div>
        </div>
      )}

      <div style={{ display:"flex", justifyContent:"flex-end", gap:10 }}>
        <Btn onClick={() => { reset(); onClose(); }} color={T.muted} outline>Cancel</Btn>
        {preview.length > 0 && errors.length === 0 && (
          <Btn onClick={handleUpload} disabled={uploading}>
            <Icon name="upload" size={14} color="white"/>
            {uploading ? `Uploading ${uploaded}/${preview.length}...` : `Upload ${preview.length} Students`}
          </Btn>
        )}
      </div>
    </Modal>
  );
};

// ── DASHBOARD ─────────────────────────────────────────────────────────────────
const Dashboard = ({ roles }) => {
  const { userProfile } = useAuth();
  const role = userProfile?.role;
  const [stats, setStats]         = useState({ students:0, paid:0, pending:0 });
  const [feeTotal, setFeeTotal]   = useState(0);
  const [feeFilter, setFeeFilter] = useState("month");
  const [feeLoading, setFeeLoading] = useState(false);
  const [loading, setLoading]     = useState(true);

  // ── Parallel fetch: students + fees at the same time ─────────────────────
  useEffect(() => {
    if (role === "parent") { setLoading(false); return; }
    const go = async () => {
      setLoading(true);
      try {
        const buildQ = () => {
          if (role === "superAdmin")     return collection(db, "students");
          if (role === "franchiseAdmin") return query(collection(db, "students"), where("center","==",userProfile?.center||""));
          if (role === "teacher")        return query(collection(db, "students"), where("teacherEmail","==",userProfile?.email||""));
          return null;
        };
        const q = buildQ();
        if (!q) { setLoading(false); return; }
        const [studentsSnap, feesSnap] = await Promise.all([
          getDocs(q),
          ["superAdmin","franchiseAdmin"].includes(role) ? getDocs(collection(db,"fees")) : Promise.resolve(null)
        ]);
        const data = studentsSnap.docs.map(d => d.data());
        setStats({ students:data.length, paid:data.filter(s=>s.fee==="Paid").length, pending:data.filter(s=>s.fee==="Pending").length });
        if (feesSnap) {
          const now = new Date();
          let fees = feesSnap.docs.map(d => d.data());
          if (role === "franchiseAdmin") fees = fees.filter(f => f.center === userProfile?.center);
          const match = fees.filter(f => {
            if (!f.paymentDate) return false;
            const d = new Date(f.paymentDate);
            if (isNaN(d.getTime())) return false;
            if (feeFilter === "today") return d.toDateString() === now.toDateString();
            if (feeFilter === "month") return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
            if (feeFilter === "year") return d.getFullYear() === now.getFullYear();
            return true;
          });
          setFeeTotal(match.reduce((s,f) => s+(Number(f.amount)||0), 0));
        }
      } catch (e) { toast.error("Dashboard failed to load: " + getFriendlyError(e)); }
      setLoading(false);
    };
    go();
  }, [userProfile, feeFilter]);

  // Separate fee filter reload
  useEffect(() => {
    if (!["superAdmin","franchiseAdmin"].includes(role)) return;
    setFeeLoading(true);
    const go = async () => {
      try {
        const snap = await getDocs(collection(db, "fees"));
        const now = new Date();
        let fees = snap.docs.map(d => d.data());
        if (role === "franchiseAdmin") fees = fees.filter(f => f.center === userProfile?.center);
        const match = fees.filter(f => {
          if (!f.paymentDate) return false;
          const d = new Date(f.paymentDate);
          if (isNaN(d.getTime())) return false;
          if (feeFilter === "today") return d.toDateString() === now.toDateString();
          if (feeFilter === "month") return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
          if (feeFilter === "year") return d.getFullYear() === now.getFullYear();
          return true;
        });
        setFeeTotal(match.reduce((s,f) => s+(Number(f.amount)||0), 0));
      } catch (e) { toast.error("An error occurred: " + getFriendlyError(e)); }
      setFeeLoading(false);
    };
    go();
  }, [feeFilter, userProfile]);

  const ri = (role && (roles?.[role] || DEFAULT_ROLES[role])) || DEFAULT_ROLES.franchiseAdmin;
  const filterLabels = { today:"Today", month:"This Month", year:"This Year", all:"All Time" };

  return (
    <div>
      <div style={{ marginBottom:22 }}>
        <h2 style={{ fontSize:21, fontWeight:800, color:T.text, marginBottom:6 }}>
          Welcome, {userProfile?.name || userProfile?.email?.split("@")[0]} {ri.emoji}
        </h2>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <RBadge role={role} roles={roles}/>
          {userProfile?.center && <Badge color={T.blue}>📍 {userProfile.center}</Badge>}
        </div>
      </div>

      <div style={{ background:`linear-gradient(135deg,${ri.color} 0%,${T.secondary} 100%)`, borderRadius:15, padding:20, marginBottom:22, color:"white" }}>
        <div style={{ fontSize:14, fontWeight:800, marginBottom:3 }}>
          {role==="superAdmin" && "🏢 Super Admin — Full System Access"}
          {role==="franchiseAdmin" && `🏫 Managing: ${userProfile?.center || "Your Center"}`}
          {role==="teacher" && "👨‍🏫 Teacher Dashboard"}
          {role==="parent" && "👨‍👩‍👧 Parent Portal"}
        </div>
        <div style={{ fontSize:12, opacity:0.85 }}>
          {role==="superAdmin" && "Full control over all centers, students, teachers, users and permissions."}
          {role==="franchiseAdmin" && "Manage students, attendance, fees and teachers for your center."}
          {role==="teacher" && "View your assigned students and mark attendance."}
          {role==="parent" && "Track your child's attendance, performance and fee status."}
        </div>
      </div>

      {role !== "parent" && (
        <>
          {loading ? (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))", gap:13, marginBottom:18 }}>
              {[1,2,3].map(i => <div key={i} style={{ background:T.card, borderRadius:13, padding:"17px 19px", border:`1px solid ${T.border}` }}><Skeleton h={46} r={12} mb={10}/><Skeleton h={12} w="60%"/><Skeleton h={28} w="40%"/></div>)}
            </div>
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))", gap:13, marginBottom:18 }}>
              <Card icon="students" label="Total Students" value={stats.students} color={T.primary}/>
              <Card icon="check"    label="Fees Paid"      value={stats.paid}     color={T.success}/>
              <Card icon="fees"     label="Fees Pending"   value={stats.pending}  color={T.rose}/>
            </div>
          )}

          {["superAdmin","franchiseAdmin"].includes(role) && (
            <div style={{ background:T.card, borderRadius:15, padding:20, border:`1px solid ${T.border}`, marginBottom:20, boxShadow:"0 2px 12px rgba(0,0,0,0.05)" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
                <div style={{ display:"flex", alignItems:"center", gap:13 }}>
                  <div style={{ width:52, height:52, borderRadius:13, background:T.success+"18", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <Icon name="dollar" size={24} color={T.success}/>
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:T.muted, fontWeight:700, textTransform:"uppercase", letterSpacing:0.6 }}>Fees Collected</div>
                    <div style={{ fontSize:30, fontWeight:900, color:T.success, lineHeight:1.1 }}>
                      {feeLoading ? "..." : `₹${feeTotal.toLocaleString("en-IN")}`}
                    </div>
                    <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>{filterLabels[feeFilter]}{role==="franchiseAdmin" && ` • ${userProfile?.center}`}</div>
                  </div>
                </div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {Object.entries(filterLabels).map(([k,l]) => (
                    <button key={k} onClick={() => setFeeFilter(k)}
                      style={{ padding:"6px 13px", borderRadius:20, border:`2px solid ${feeFilter===k?T.success:T.border}`, background:feeFilter===k?T.success:"white", color:feeFilter===k?"white":T.text, fontWeight:700, fontSize:12, cursor:"pointer", transition:"all 0.15s" }}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div style={{ background:T.card, borderRadius:15, padding:20, border:`1px solid ${T.border}` }}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:13, color:T.text, display:"flex", alignItems:"center", gap:7 }}>
          <Icon name="shield" size={15} color={ri.color}/> Your Access Permissions
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(165px,1fr))", gap:7 }}>
          {[
            { label:"View All Centers",    ok:role==="superAdmin" },
            { label:"Manage All Students", ok:["superAdmin","franchiseAdmin"].includes(role) },
            { label:"Mark Attendance",     ok:["superAdmin","franchiseAdmin","teacher"].includes(role) },
            { label:"Record Fees",         ok:["superAdmin","franchiseAdmin"].includes(role) },
            { label:"Manage Teachers",     ok:["superAdmin","franchiseAdmin"].includes(role) },
            { label:"Bulk Upload",         ok:["superAdmin","franchiseAdmin"].includes(role) },
            { label:"Manage Franchises",   ok:role==="superAdmin" },
            { label:"Manage Users",        ok:role==="superAdmin" },
            { label:"Edit Permissions",    ok:role==="superAdmin" },
            { label:"View Child Data",     ok:role==="parent" },
          ].map(({ label, ok }) => (
            <div key={label} style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 9px", background:ok?T.success+"10":T.rose+"08", borderRadius:8, border:`1px solid ${ok?T.success+"30":T.rose+"20"}` }}>
              <Icon name={ok?"check":"x"} size={13} color={ok?T.success:T.rose}/>
              <span style={{ fontSize:11, fontWeight:600, color:ok?T.success:T.muted }}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── STUDENTS PAGE ─────────────────────────────────────────────────────────────
const StudentsPage = ({ roles }) => {
  const { userProfile } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(false);
  const [bulk, setBulk]         = useState(false);
  const [editSt, setEditSt]     = useState(null);
  const [search, setSearch]     = useState("");
  const [saving, setSaving]     = useState(false);
  const [page, setPage]         = useState(0);
  const [form, setForm] = useState({ name:"", age:"", grade:"", course:"", batch:"", parentName:"", parentEmail:"", phone:"", fee:"Pending", center:"", teacherEmail:"" });
  const isSA    = userProfile?.role === "superAdmin";
  const canEdit = ["superAdmin","franchiseAdmin"].includes(userProfile?.role);

  const cacheKey = useCallback(() => {
    const r = userProfile?.role;
    if (r==="superAdmin")     return "students-sa";
    if (r==="franchiseAdmin") return "students-fa-"+(userProfile?.center||"");
    if (r==="teacher")        return "students-t-"+(userProfile?.email||"");
    return null;
  }, [userProfile]);

  const load = useCallback(async (forceRefresh=false) => {
    setLoading(true);
    try {
      const r = userProfile?.role;
      const key = cacheKey();
      if (!key) { setStudents([]); setLoading(false); return; }

      if (forceRefresh) bust(key);

      const buildQ = () => {
        if (r==="superAdmin")     return collection(db,"students");
        if (r==="franchiseAdmin") return query(collection(db,"students"), where("center","==",userProfile?.center||""));
        if (r==="teacher")        return query(collection(db,"students"), where("teacherEmail","==",userProfile?.email||""));
        return null;
      };
      const q = buildQ();
      if (!q) { setStudents([]); setLoading(false); return; }

      const data = await cachedDocs(key, () => q);
      setStudents([...data].sort((a,b) => (b.createdAt?.seconds||0)-(a.createdAt?.seconds||0)));
    } catch (e) {
      toast.error("Failed to load students: " + getFriendlyError(e));
      toast.error("Failed to load students: " + getFriendlyError(e));
    }
    setLoading(false);
  }, [userProfile, cacheKey]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setEditSt(null); setForm({ name:"",age:"",grade:"",course:"",batch:"",parentName:"",parentEmail:"",phone:"",fee:"Pending",center:userProfile?.center||"",teacherEmail:"" }); setModal(true); };
  const openEdit = s => { setEditSt(s); setForm({ name:s.name||"",age:s.age||"",grade:s.grade||"",course:s.course||"",batch:s.batch||"",parentName:s.parentName||"",parentEmail:s.parentEmail||"",phone:s.phone||"",fee:s.fee||"Pending",center:s.center||"",teacherEmail:s.teacherEmail||"" }); setModal(true); };

  const save = async () => {
    if (!form.name?.trim() || !form.course || !form.batch) { toast.error("Name, Course and Batch are required"); return; }
    if (form.name.trim().length < 2) { toast.error("Student name must be at least 2 characters"); return; }
    if (form.phone && !/^\d{10}$/.test(form.phone)) { toast.error("Phone must be exactly 10 digits"); return; }
    if (form.parentEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.parentEmail)) { toast.error("Enter a valid Parent Email"); return; }
    setSaving(true);
    try {
      const data = { ...form, center:isSA?form.center:(userProfile?.center||"") };

      // ── Helper: auto-create parent Firebase Auth + Firestore profile ──────
      const ensureParentAccount = async (pEmail, parentName, center) => {
        if (!pEmail?.trim()) return;
        pEmail = pEmail.trim().toLowerCase();
        try {
          const existing = await getDocs(query(collection(db,"users"), where("email","==",pEmail)));
          if (!existing.empty) { toast.info?.("Parent login already exists for " + pEmail); return; }
          const secAuth = _getSecondaryAuth();
          if (!secAuth) throw new Error("Secondary auth unavailable");
          const pCred = await createUserWithEmailAndPassword(secAuth, pEmail, "mistermind");
          await addDoc(collection(db,"users"), {
            uid: pCred.user.uid, email: pEmail,
            name: parentName?.trim() || "Parent/Guardian",
            role: "parent", center: center||"",
            active: true, locked: false, firstLogin: true,
            createdAt: serverTimestamp(), createdBy: userProfile?.email,
          });
          await signOut(secAuth);
          toast.success("Parent login created — Email: " + pEmail + " | Password: mistermind");
        } catch (pe) {
          if (pe.code === "auth/email-already-in-use") {
            // Firebase Auth exists but Firestore profile missing — safe to ignore
          } else {
            toast.warn("Parent login creation failed: " + getFriendlyError(pe));
          }
        }
      };

      if (editSt) {
        await updateDoc(doc(db,"students",editSt.id), { ...data, updatedAt:serverTimestamp() });
        toast.success("Student updated successfully.");
        // If parentEmail was newly added or changed, create account
        const oldEmail = (editSt.parentEmail||"").toLowerCase();
        const newEmail = (data.parentEmail||"").toLowerCase();
        if (newEmail && newEmail !== oldEmail) {
          await ensureParentAccount(data.parentEmail, data.parentName, data.center);
        }
      } else {
        await addDoc(collection(db,"students"), { ...data, studentId:"AVM-"+Date.now()+"-"+Math.random().toString(36).slice(2,6).toUpperCase(), attendance:0, createdAt:serverTimestamp() });
        toast.success("Student enrolled successfully.");
        await ensureParentAccount(data.parentEmail, data.parentName, data.center);
      }
      await load(true); setModal(false);
    } catch (e) { toast.error("Error: " + getFriendlyError(e)); }
    setSaving(false);
  };

  const del = async id => {
    if (!isSA) { toast.warn("Only Super Admin can delete students"); return; }
    if (!window.confirm("Delete this student? This cannot be undone.")) return;
    try {
      await deleteDoc(doc(db,"students",id));
      await load(true);
      toast.success("Student removed.");
    } catch (e) { toast.error("Error: " + getFriendlyError(e)); }
  };

  const filtered = useMemo(() => students.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.studentId?.toLowerCase().includes(search.toLowerCase()) ||
    s.parentName?.toLowerCase().includes(search.toLowerCase())
  ), [students, search]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = useMemo(() => filtered.slice(page*PAGE_SIZE, (page+1)*PAGE_SIZE), [filtered, page]);

  useEffect(() => { setPage(0); }, [search]);

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:22, flexWrap:"wrap", gap:12 }}>
        <div>
          <h2 style={{ fontSize:21, fontWeight:800, color:T.text, marginBottom:4 }}>
            {userProfile?.role==="teacher" ? "My Students" : "Student Management"}
          </h2>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <span style={{ color:T.muted, fontSize:13 }}>{filtered.length} students</span>
            {!isSA && userProfile?.center && <Badge color={T.blue}>📍 {userProfile.center}</Badge>}
          </div>
        </div>
        {canEdit && (
          <div style={{ display:"flex", gap:9 }}>
            <Btn onClick={() => setBulk(true)} color={T.blue} outline sm><Icon name="upload" size={13} color={T.blue}/> Bulk Upload</Btn>
            <Btn onClick={openAdd}><Icon name="plus" size={14} color="white"/> Enroll Student</Btn>
          </div>
        )}
      </div>

      <div style={{ background:T.card, borderRadius:15, padding:18, border:`1px solid ${T.border}` }}>
        <div style={{ background:T.bg, borderRadius:8, padding:"8px 13px", display:"flex", alignItems:"center", gap:8, border:`1px solid ${T.border}`, marginBottom:15 }}>
          <Icon name="search" size={14} color={T.muted}/>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, ID or parent..."
            style={{ border:"none", background:"transparent", fontSize:13, color:T.text, outline:"none", width:"100%" }}/>
        </div>

        {loading ? <TableSkeleton rows={5} cols={canEdit?9:8}/> : filtered.length === 0 ? (
          <div style={{ textAlign:"center", padding:36, color:T.muted }}>
            <div style={{ fontSize:34, marginBottom:9 }}>👨‍🎓</div>
            <div style={{ fontWeight:700, marginBottom:8 }}>No students found</div>
            {canEdit && <Btn onClick={openAdd} sm><Icon name="plus" size={13} color="white"/> Enroll First Student</Btn>}
          </div>
        ) : (
          <>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead>
                  <tr style={{ background:T.bg }}>
                    {["ID","Name","Grade","Course","Batch","Center","Parent","Phone","Fee",...(canEdit?["Actions"]:[])].map(h => (
                      <th key={h} style={{ padding:"8px 11px", textAlign:"left", color:T.muted, fontWeight:700, fontSize:10, textTransform:"uppercase", letterSpacing:0.5, whiteSpace:"nowrap", borderBottom:`1px solid ${T.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(s => (
                    <tr key={s.id} style={{ borderBottom:`1px solid ${T.border}` }}
                      onMouseEnter={e => e.currentTarget.style.background=T.bg}
                      onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                      <td style={{ padding:"9px 11px", fontFamily:"monospace", fontSize:10, color:T.purple, fontWeight:700 }}>{s.studentId}</td>
                      <td style={{ padding:"9px 11px", fontWeight:700, color:T.text, whiteSpace:"nowrap" }}>{s.name}</td>
                      <td style={{ padding:"9px 11px", color:T.text }}>{s.grade}</td>
                      <td style={{ padding:"9px 11px" }}><Badge color={s.course?.includes("Abacus")?T.primary:T.teal}>{s.course}</Badge></td>
                      <td style={{ padding:"9px 11px", whiteSpace:"nowrap", color:T.text }}>{s.batch}</td>
                      <td style={{ padding:"9px 11px" }}>{s.center ? <Badge color={T.blue}>{s.center}</Badge> : "—"}</td>
                      <td style={{ padding:"9px 11px", color:T.text, whiteSpace:"nowrap" }}>{s.parentName||"—"}</td>
                      <td style={{ padding:"9px 11px", color:T.text }}>{s.phone||"—"}</td>
                      <td style={{ padding:"9px 11px" }}><SBadge status={s.fee||"Pending"}/></td>
                      {canEdit && (
                        <td style={{ padding:"9px 11px" }}>
                          <div style={{ display:"flex", gap:5 }}>
                            <Btn sm onClick={() => openEdit(s)} color={T.blue} outline><Icon name="edit" size={11} color={T.blue}/> Edit</Btn>
                            {isSA && <Btn sm onClick={() => del(s.id)} color={T.rose} outline><Icon name="trash" size={11} color={T.rose}/></Btn>}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display:"flex", justifyContent:"center", gap:8, marginTop:14, flexWrap:"wrap" }}>
                <Btn sm onClick={() => setPage(p => Math.max(0,p-1))} disabled={page===0} color={T.muted} outline>← Prev</Btn>
                <span style={{ color:T.muted, fontSize:12, alignSelf:"center" }}>Page {page+1} of {totalPages}</span>
                <Btn sm onClick={() => setPage(p => Math.min(totalPages-1,p+1))} disabled={page===totalPages-1} color={T.muted} outline>Next →</Btn>
              </div>
            )}
          </>
        )}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editSt ? "Edit Student" : "Enroll New Student"}>
        <Inp label="Student Name" value={form.name} onChange={e => setForm({...form,name:e.target.value})} placeholder="Full name" required/>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <Inp label="Age" type="number" value={form.age} onChange={e => setForm({...form,age:e.target.value})} placeholder="3-18"/>
          <Sel label="Grade" value={form.grade} onChange={e => setForm({...form,grade:e.target.value})} options={GRADES}/>
        </div>
        <Inp label="Parent Name" value={form.parentName} onChange={e => setForm({...form,parentName:e.target.value})} placeholder="Guardian name"/>
        <Inp label="Parent Email (Login ID)" type="email" value={form.parentEmail} onChange={e => setForm({...form,parentEmail:e.target.value})} placeholder="parent@email.com — used for Child/Parent login"/>
        <Inp label="Phone (10 digits)" type="tel" value={form.phone} onChange={e => setForm({...form,phone:e.target.value})} placeholder="9XXXXXXXXX"/>
        <Sel label="Course" value={form.course} onChange={e => setForm({...form,course:e.target.value})} options={COURSES} required/>
        <Sel label="Batch" value={form.batch} onChange={e => setForm({...form,batch:e.target.value})} options={BATCHES} required/>
        {isSA && <Inp label="Center Name" value={form.center} onChange={e => setForm({...form,center:e.target.value})} placeholder="e.g. Powai Center"/>}
        {isSA && <Inp label="Assigned Teacher Email" value={form.teacherEmail} onChange={e => setForm({...form,teacherEmail:e.target.value})} placeholder="teacher@email.com"/>}
        <Sel label="Fee Status" value={form.fee} onChange={e => setForm({...form,fee:e.target.value})} options={FEE_ST}/>

        {/* Resend parent login — shown only in edit mode when parentEmail exists */}
        {editSt && form.parentEmail?.trim() && (
          <div style={{ background:T.blue+"10", border:`1px solid ${T.blue}30`, borderRadius:9, padding:"10px 13px", marginTop:6, fontSize:12, color:T.blue }}>
            👨‍👩‍👧 <strong>Parent Login:</strong> {form.parentEmail}
            <Btn sm onClick={async () => {
              const pEmail = form.parentEmail.trim().toLowerCase();
              try {
                const secAuth = _getSecondaryAuth();
                if (!secAuth) { toast.error("Could not initialise secondary auth — try again."); return; }

                // Step 1: Always try to create the Firebase Auth account
                let authUid = null;
                try {
                  const pCred = await createUserWithEmailAndPassword(secAuth, pEmail, "mistermind");
                  authUid = pCred.user.uid;
                  await signOut(secAuth);
                } catch(authErr) {
                  if (authErr.code === "auth/email-already-in-use") {
                    // Auth account exists — that's fine, just ensure Firestore profile exists below
                  } else {
                    toast.error("Auth creation failed: " + getFriendlyError(authErr));
                    return;
                  }
                }

                // Step 2: Check / create Firestore profile
                const existing = await getDocs(query(collection(db,"users"), where("email","==",pEmail)));
                if (existing.empty) {
                  await addDoc(collection(db,"users"), {
                    uid: authUid || "", email: pEmail,
                    name: form.parentName?.trim() || "Parent/Guardian",
                    role: "parent", center: form.center || editSt?.center || "",
                    active: true, locked: false, firstLogin: true,
                    createdAt: serverTimestamp(), createdBy: userProfile?.email,
                  });
                  toast.success("Parent login created! Email: " + pEmail + " | Password: mistermind");
                } else {
                  // Profile exists — update uid if we just got one, and reset firstLogin
                  const profileDoc = existing.docs[0];
                  const updates = { firstLogin: true, active: true, locked: false };
                  if (authUid) updates.uid = authUid;
                  await updateDoc(doc(db,"users", profileDoc.id), updates);
                  toast.success("Parent login is ready. Email: " + pEmail + " | Password: mistermind");
                }
              } catch(pe) {
                toast.error("Failed: " + getFriendlyError(pe));
              }
            }} color={T.blue} style={{ marginLeft:10 }}>
              Create / Resend Login
            </Btn>
          </div>
        )}

        <div style={{ display:"flex", gap:9, marginTop:10 }}>
          <Btn onClick={() => setModal(false)} color={T.muted} outline>Cancel</Btn>
          <Btn onClick={save} disabled={saving}><Icon name="save" size={14} color="white"/>{saving ? "Saving..." : editSt ? "Update Student" : "Enroll Student"}</Btn>
        </div>
      </Modal>

      <BulkUploadModal open={bulk} onClose={() => setBulk(false)} userProfile={userProfile} onSuccess={() => load(true)}/>
    </div>
  );
};

// ── ATTENDANCE PAGE ───────────────────────────────────────────────────────────
const AttendancePage = () => {
  const { userProfile } = useAuth();
  const [students, setStudents] = useState([]);
  const [att, setAtt]           = useState({});
  const [date, setDate]         = useState(new Date().toISOString().split("T")[0]);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState(false);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    const go = async () => {
      setLoading(true);
      try {
        const r = userProfile?.role;
        let q;
        if (r==="superAdmin")      q = collection(db,"students");
        else if (r==="franchiseAdmin") q = query(collection(db,"students"), where("center","==",userProfile?.center||""));
        else if (r==="teacher")    q = query(collection(db,"students"), where("teacherEmail","==",userProfile?.email||""));
        else { setStudents([]); setLoading(false); return; }
        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({ id:d.id, ...d.data() }));
        setStudents(data);
        const init = {}; data.forEach(s => { init[s.id] = "Present"; });
        setAtt(init);
      } catch (e) {
        toast.error("Failed to load students: " + getFriendlyError(e));
      }
      setLoading(false);
    };
    go();
  }, [userProfile]);

  const toggle = id => setAtt(p => ({ ...p, [id]:p[id]==="Present" ? "Absent" : "Present" }));

  // ── Parallel attendance writes (10 at a time) ────────────────────────────
  const saveAtt = async () => {
    setSaving(true);
    const CHUNK = 10;
    try {
      for (let i = 0; i < students.length; i += CHUNK) {
        const chunk = students.slice(i, i + CHUNK);
        await Promise.all(chunk.map(s =>
          addDoc(collection(db,"attendance"), {
            studentId:    s.id,
            studentName:  s.name,
            date, batch:  s.batch||"",
            center:       s.center||"",
            status:       att[s.id] || "Present",
            markedBy:     userProfile?.email,
            markedByRole: userProfile?.role,
            createdAt:    serverTimestamp(),
          })
        ));
      }
      setSaved(true);
      toast.success("Attendance saved for " + students.length + " students!");
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      toast.error("Error saving attendance: " + getFriendlyError(e));
    }
    setSaving(false);
  };

  const present = Object.values(att).filter(v => v==="Present").length;

  return (
    <div>
      <div style={{ marginBottom:18 }}>
        <h2 style={{ fontSize:21, fontWeight:800, color:T.text, marginBottom:4 }}>Attendance Management</h2>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <span style={{ color:T.muted, fontSize:13 }}>Tap a card to toggle Present / Absent</span>
          {userProfile?.role==="franchiseAdmin" && <Badge color={T.blue}>📍 {userProfile.center}</Badge>}
          {userProfile?.role==="teacher" && <Badge color={T.purple}>Your students only</Badge>}
        </div>
      </div>

      <div style={{ marginBottom:16 }}>
        <label style={{ fontSize:11, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:0.5, display:"block", marginBottom:5 }}>Select Date</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          style={{ padding:"8px 11px", border:`1px solid ${T.border}`, borderRadius:8, fontSize:13, color:T.text, outline:"none" }}/>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:11, marginBottom:18 }}>
        {[{l:"Present",v:present,c:T.success},{l:"Absent",v:students.length-present,c:T.rose},{l:"Total",v:students.length,c:T.blue}].map(s => (
          <div key={s.l} style={{ background:T.card, borderRadius:12, padding:15, border:`1px solid ${T.border}`, textAlign:"center" }}>
            <div style={{ fontSize:24, fontWeight:800, color:s.c }}>{s.v}</div>
            <div style={{ fontSize:11, color:T.muted, fontWeight:600, marginTop:2 }}>{s.l}</div>
          </div>
        ))}
      </div>

      {loading ? <TableSkeleton rows={4} cols={3}/> : students.length===0 ? (
        <div style={{ background:T.card, borderRadius:15, padding:40, border:`1px solid ${T.border}`, textAlign:"center", color:T.muted }}>
          <div style={{ fontSize:34, marginBottom:9 }}>📋</div>
          <div style={{ fontWeight:700 }}>No students found</div>
          <div style={{ fontSize:12, marginTop:4 }}>{userProfile?.role==="teacher" ? "No students assigned to you yet." : "Add students from the Students page first."}</div>
        </div>
      ) : (
        <div style={{ background:T.card, borderRadius:15, padding:18, border:`1px solid ${T.border}` }}>
          <div style={{ fontWeight:700, fontSize:14, color:T.text, marginBottom:13 }}>Mark Attendance — {date}</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:9, marginBottom:16 }}>
            {students.map(s => (
              <div key={s.id} onClick={() => toggle(s.id)}
                style={{ background:att[s.id]==="Present"?T.success+"10":T.rose+"10", border:`2px solid ${att[s.id]==="Present"?T.success:T.rose}`, borderRadius:11, padding:13, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between", transition:"all 0.15s" }}>
                <div>
                  <div style={{ fontWeight:700, color:T.text, fontSize:13 }}>{s.name}</div>
                  <div style={{ fontSize:10, color:T.muted }}>{s.batch}{s.center && ` • ${s.center}`}</div>
                </div>
                <div style={{ width:28, height:28, borderRadius:8, background:(att[s.id]==="Present"?T.success:T.rose)+"25", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <Icon name={att[s.id]==="Present"?"check":"x"} size={14} color={att[s.id]==="Present"?T.success:T.rose}/>
                </div>
              </div>
            ))}
          </div>
          {saved && <div style={{ background:T.success+"15", border:`1px solid ${T.success}40`, borderRadius:8, padding:"9px 13px", color:T.success, fontWeight:700, fontSize:12, marginBottom:13 }}>✅ Attendance saved successfully!</div>}
          <div style={{ display:"flex", justifyContent:"flex-end" }}>
            <Btn onClick={saveAtt} disabled={saving}><Icon name="save" size={14} color="white"/>{saving ? "Saving..." : "Save Attendance"}</Btn>
          </div>
        </div>
      )}
    </div>
  );
};

// ── FEES PAGE ─────────────────────────────────────────────────────────────────
const FeesPage = () => {
  const { userProfile } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(false);
  const [receiptModal, setReceiptModal] = useState(false);
  const [receiptStudent, setReceiptStudent] = useState(null);
  const [receiptPayments, setReceiptPayments] = useState([]);
  const [sel, setSel]           = useState(null);
  const [saving, setSaving]     = useState(false);
  const [form, setForm] = useState({ amount:"", paymentDate:new Date().toISOString().split("T")[0], paymentMode:"Cash", notes:"" });
  const canRecord = ["superAdmin","franchiseAdmin"].includes(userProfile?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = userProfile?.role;
      let q;
      if (r==="superAdmin")      q = collection(db,"students");
      else if (r==="franchiseAdmin") q = query(collection(db,"students"), where("center","==",userProfile?.center||""));
      else if (r==="parent")     q = query(collection(db,"students"), where("parentEmail","==",userProfile?.email?.toLowerCase()||""));
      else { setLoading(false); return; }
      const snap = await getDocs(q);
      setStudents(snap.docs.map(d => ({ id:d.id, ...d.data() })));
    } catch (e) { toast.error("Failed to load: " + getFriendlyError(e)); }
    setLoading(false);
  }, [userProfile]);

  useEffect(() => { load(); }, [load]);

  const openPay = s => { setSel(s); setForm({ amount:"", paymentDate:new Date().toISOString().split("T")[0], paymentMode:"Cash", notes:"" }); setModal(true); };

  const openReceipt = async s => {
    try {
      const snap = await getDocs(query(collection(db,"fees"), where("studentId","==",s.id)));
      const pays = snap.docs.map(d => d.data()).sort((a,b) => new Date(b.paymentDate)-new Date(a.paymentDate));
      setReceiptStudent(s); setReceiptPayments(pays); setReceiptModal(true);
    } catch(e) { toast.error("Could not load payments: " + getFriendlyError(e)); }
  };

  const savePay = async () => {
    if (!form.amount || isNaN(form.amount) || +form.amount <= 0 || +form.amount > 1000000) { toast.error("Enter a valid amount (₹1 – ₹10,00,000)"); return; }
    setSaving(true);
    try {
      await addDoc(collection(db,"fees"), { studentId:sel.id, studentName:sel.name, center:sel.center||"", recordedBy:userProfile?.email, recordedByRole:userProfile?.role, ...form, createdAt:serverTimestamp() });
      await updateDoc(doc(db,"students",sel.id), { fee:"Paid" });
      toast.success("Payment recorded for " + sel.name);
      await load(); setModal(false);
    } catch (e) { toast.error("Error: " + getFriendlyError(e)); }
    setSaving(false);
  };

  const paid    = students.filter(s => s.fee==="Paid").length;
  const pending = students.filter(s => s.fee==="Pending").length;

  return (
    <div>
      <div style={{ marginBottom:22 }}>
        <h2 style={{ fontSize:21, fontWeight:800, color:T.text, marginBottom:4 }}>Fees & Payments</h2>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <span style={{ color:T.muted, fontSize:13 }}>Track collections</span>
          {userProfile?.role==="franchiseAdmin" && <Badge color={T.blue}>📍 {userProfile.center} only</Badge>}
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(155px,1fr))", gap:13, marginBottom:20 }}>
        <Card icon="check"    label="Paid"    value={paid}             color={T.success}/>
        <Card icon="fees"     label="Pending" value={pending}          color={T.rose}/>
        <Card icon="students" label="Total"   value={students.length}  color={T.blue}/>
      </div>
      <div style={{ background:T.card, borderRadius:15, padding:18, border:`1px solid ${T.border}` }}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:13, color:T.text }}>Student Fee Status</div>
        {loading ? <TableSkeleton rows={4} cols={canRecord?7:6}/> : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr style={{ background:T.bg }}>
                  {["Student","Course","Center","Parent","Phone","Status",...(canRecord?["Action","Receipt"]:[])].map(h => (
                    <th key={h} style={{ padding:"8px 11px", textAlign:"left", color:T.muted, fontWeight:700, fontSize:10, textTransform:"uppercase", borderBottom:`1px solid ${T.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {students.map(s => (
                  <tr key={s.id} style={{ borderBottom:`1px solid ${T.border}` }}
                    onMouseEnter={e => e.currentTarget.style.background=T.bg}
                    onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                    <td style={{ padding:"9px 11px", fontWeight:700, color:T.text }}>{s.name}</td>
                    <td style={{ padding:"9px 11px" }}><Badge color={s.course?.includes("Abacus")?T.primary:T.teal}>{s.course}</Badge></td>
                    <td style={{ padding:"9px 11px" }}>{s.center ? <Badge color={T.blue}>{s.center}</Badge> : "—"}</td>
                    <td style={{ padding:"9px 11px", color:T.text }}>{s.parentName}</td>
                    <td style={{ padding:"9px 11px", color:T.text }}>{s.phone}</td>
                    <td style={{ padding:"9px 11px" }}><SBadge status={s.fee||"Pending"}/></td>
                    {canRecord && <td style={{ padding:"9px 11px" }}>
                      <Btn sm onClick={() => openPay(s)} color={T.success}><Icon name="dollar" size={11} color="white"/> Record</Btn>
                    </td>}
                    {canRecord && <td style={{ padding:"9px 11px" }}>
                      <Btn sm onClick={() => openReceipt(s)} color={T.blue}><Icon name="save" size={11} color="white"/> Receipt</Btn>
                    </td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Modal open={modal} onClose={() => setModal(false)} title={`Record Payment — ${sel?.name}`}>
        <Inp label="Amount (₹)" type="number" value={form.amount} onChange={e => setForm({...form,amount:e.target.value})} placeholder="Enter amount" required/>
        <Inp label="Payment Date" type="date" value={form.paymentDate} onChange={e => setForm({...form,paymentDate:e.target.value})}/>
        <Sel label="Payment Mode" value={form.paymentMode} onChange={e => setForm({...form,paymentMode:e.target.value})} options={PAY_MODE}/>
        <Inp label="Notes (Optional)" value={form.notes} onChange={e => setForm({...form,notes:e.target.value})} placeholder="Any notes"/>
        <div style={{ display:"flex", gap:9, marginTop:10 }}>
          <Btn onClick={() => setModal(false)} color={T.muted} outline>Cancel</Btn>
          <Btn onClick={savePay} disabled={saving}><Icon name="save" size={14} color="white"/>{saving ? "Saving..." : "Save Payment"}</Btn>
        </div>
      </Modal>
      <FeeReceiptModal open={receiptModal} onClose={() => setReceiptModal(false)}
        student={receiptStudent} payments={receiptPayments}
        franchiseName={receiptStudent?.center || "Mistermind Abacus"}/>
    </div>
  );
};

// ── MANAGE TEACHERS ───────────────────────────────────────────────────────────
const ManageTeachers = () => {
  const { userProfile } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(false);
  const [editT, setEditT]       = useState(null);
  const [saving, setSaving]     = useState(false);
  const [form, setForm] = useState({ name:"", email:"", phone:"", specialization:"Abacus", experience:"", batches:"", center:"" });
  const isSA = userProfile?.role === "superAdmin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = isSA ? collection(db,"teachers") : query(collection(db,"teachers"), where("center","==",userProfile?.center||""));
      const snap = await getDocs(q);
      setTeachers(snap.docs.map(d => ({ id:d.id, ...d.data() })));
    } catch (e) { toast.error("Failed to load teachers: " + getFriendlyError(e)); }
    setLoading(false);
  }, [userProfile, isSA]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setEditT(null); setForm({ name:"",email:"",phone:"",specialization:"Abacus",experience:"",batches:"",center:userProfile?.center||"" }); setModal(true); };
  const openEdit = t => { setEditT(t); setForm({ name:t.name||"",email:t.email||"",phone:t.phone||"",specialization:t.specialization||"Abacus",experience:t.experience||"",batches:t.batches||"",center:t.center||"" }); setModal(true); };

  const save = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    if (!/\S+@\S+\.\S+/.test(form.email)) { toast.error("Enter a valid email address"); return; }
    setSaving(true);
    try {
      const data = { ...form, center:isSA?form.center:(userProfile?.center||""), updatedAt:serverTimestamp() };
      if (editT) {
        await updateDoc(doc(db,"teachers",editT.id), data);
        toast.success("Teacher updated.");
      } else {
        await addDoc(collection(db,"teachers"), { ...data, createdAt:serverTimestamp() });
        toast.success("Teacher added.");
      }
      await load(); setModal(false);
    } catch (e) { toast.error("Error: " + getFriendlyError(e)); }
    setSaving(false);
  };

  const del = async id => {
    if (!window.confirm("Remove this teacher?")) return;
    try {
      await deleteDoc(doc(db,"teachers",id));
      await load();
      toast.success("Teacher removed.");
    } catch (e) { toast.error("Error: " + getFriendlyError(e)); }
  };

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:22, flexWrap:"wrap", gap:12 }}>
        <div>
          <h2 style={{ fontSize:21, fontWeight:800, color:T.text, marginBottom:4 }}>Teacher Management</h2>
          <div style={{ display:"flex", gap:8 }}>
            <span style={{ color:T.muted, fontSize:13 }}>{teachers.length} teachers</span>
            {!isSA && <Badge color={T.blue}>📍 {userProfile?.center}</Badge>}
          </div>
        </div>
        <Btn onClick={openAdd}><Icon name="plus" size={14} color="white"/> Add Teacher</Btn>
      </div>
      <div style={{ background:T.blue+"10", border:`1px solid ${T.blue}30`, borderRadius:9, padding:"10px 14px", marginBottom:16, fontSize:12, color:T.blue }}>
        💡 After adding a teacher here, share their email. They must be added as a user (via Manage Users) with the Teacher role to access the portal.
      </div>
      {loading ? <TableSkeleton rows={3} cols={4}/> : teachers.length===0 ? (
        <div style={{ background:T.card, borderRadius:15, padding:40, border:`1px solid ${T.border}`, textAlign:"center" }}>
          <div style={{ fontSize:34, marginBottom:10 }}>👨‍🏫</div>
          <div style={{ fontWeight:700, color:T.text, marginBottom:11 }}>No teachers yet</div>
          <Btn onClick={openAdd}><Icon name="plus" size={14} color="white"/> Add First Teacher</Btn>
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(265px,1fr))", gap:13 }}>
          {teachers.map(t => (
            <div key={t.id} style={{ background:T.card, borderRadius:15, padding:19, border:`1px solid ${T.border}` }}>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:13 }}>
                <div style={{ width:44, height:44, borderRadius:12, background:T.purple+"20", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>
                  {t.name?.toLowerCase().includes("ms")||t.name?.toLowerCase().includes("mrs") ? "👩‍🏫" : "👨‍🏫"}
                </div>
                <div>
                  <div style={{ fontWeight:800, color:T.text, fontSize:14 }}>{t.name}</div>
                  <Badge color={t.specialization?.includes("Vedic")?T.teal:T.primary}>{t.specialization||"Abacus"}</Badge>
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:11 }}>
                {[{l:"Experience",v:t.experience||"—",c:T.blue},{l:"Batches",v:t.batches||"—",c:T.purple}].map(s => (
                  <div key={s.l} style={{ background:T.bg, borderRadius:8, padding:8, textAlign:"center" }}>
                    <div style={{ fontWeight:800, fontSize:13, color:s.c }}>{s.v}</div>
                    <div style={{ fontSize:9, color:T.muted, fontWeight:600 }}>{s.l}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize:11, color:T.muted, marginBottom:2 }}>📧 {t.email}</div>
              {t.phone && <div style={{ fontSize:11, color:T.muted, marginBottom:2 }}>📱 {t.phone}</div>}
              {isSA && t.center && <div style={{ marginTop:6 }}><Badge color={T.blue}>📍 {t.center}</Badge></div>}
              <div style={{ display:"flex", gap:7, marginTop:11 }}>
                <Btn sm onClick={() => openEdit(t)} color={T.blue} outline><Icon name="edit" size={11} color={T.blue}/> Edit</Btn>
                <Btn sm onClick={() => del(t.id)} color={T.rose} outline><Icon name="trash" size={11} color={T.rose}/></Btn>
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal open={modal} onClose={() => setModal(false)} title={editT ? "Edit Teacher" : "Add New Teacher"}>
        <Inp label="Full Name" value={form.name} onChange={e => setForm({...form,name:e.target.value})} placeholder="Teacher's full name" required/>
        <Inp label="Email Address" type="email" value={form.email} onChange={e => setForm({...form,email:e.target.value})} placeholder="teacher@email.com" required/>
        <Inp label="Phone Number" type="tel" value={form.phone} onChange={e => setForm({...form,phone:e.target.value})} placeholder="+91 9XXXXXXXXX"/>
        <Sel label="Specialization" value={form.specialization} onChange={e => setForm({...form,specialization:e.target.value})} options={["Abacus","Vedic Maths","Abacus & Vedic Maths"]}/>
        <Inp label="Experience" value={form.experience} onChange={e => setForm({...form,experience:e.target.value})} placeholder="e.g. 3 years"/>
        <Inp label="Assigned Batches" value={form.batches} onChange={e => setForm({...form,batches:e.target.value})} placeholder="e.g. Morning A, Evening B"/>
        {isSA && <Inp label="Center Name" value={form.center} onChange={e => setForm({...form,center:e.target.value})} placeholder="e.g. Powai Center"/>}
        <div style={{ background:T.warning+"15", border:`1px solid ${T.warning}40`, borderRadius:8, padding:"9px 12px", fontSize:12, color:T.warning, marginBottom:13 }}>
          ⚠️ To give this teacher portal access, go to Manage Users and add them with the Teacher role using this email.
        </div>
        <div style={{ display:"flex", gap:9 }}>
          <Btn onClick={() => setModal(false)} color={T.muted} outline>Cancel</Btn>
          <Btn onClick={save} disabled={saving}><Icon name="save" size={14} color="white"/>{saving ? "Saving..." : editT ? "Update Teacher" : "Add Teacher"}</Btn>
        </div>
      </Modal>
    </div>
  );
};

// ── RBAC EDITOR (Super Admin Only) ────────────────────────────────────────────
const RBACEditor = ({ roles, setRoles }) => {
  const { userProfile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  if (userProfile?.role !== "superAdmin") return <Denied/>;

  const ALL_PAGES = [
    { key:"dashboard",     label:"Dashboard" },
    { key:"students",      label:"Students (All)" },
    { key:"myStudents",    label:"My Students (Teacher view)" },
    { key:"attendance",    label:"Attendance" },
    { key:"fees",          label:"Fees & Payments" },
    { key:"manageTeachers",label:"Manage Teachers" },
    { key:"franchise",     label:"Franchises" },
    { key:"media",         label:"Media Library" },
    { key:"practice",      label:"Practice Test (Child/Parent)" },
    { key:"reports",       label:"Reports" },
    { key:"manageUsers",   label:"Manage Users" },
    { key:"rbacEditor",    label:"Permission Editor" },
    { key:"myChild",       label:"My Child (Parent)" },
  ];
  const ROLE_KEYS = Object.keys(DEFAULT_ROLES);
  const LOCKED = { superAdmin:["dashboard","manageUsers","rbacEditor"] };

  const toggle = (rk, pk) => {
    if (LOCKED[rk]?.includes(pk)) return;
    const curr = roles[rk]?.pages || [];
    const pages = curr.includes(pk) ? curr.filter(p => p!==pk) : [...curr, pk];
    setRoles(prev => ({ ...prev, [rk]:{ ...prev[rk], pages } }));
  };

  const saveRBAC = async () => {
    setSaving(true);
    try {
      const config = {};
      ROLE_KEYS.forEach(k => { config[k] = { pages:roles[k]?.pages||[] }; });
      await setDoc(doc(db,"config","rbac"), { roles:config, updatedAt:serverTimestamp(), updatedBy:userProfile?.email });
      setSaved(true);
      toast.success("Permissions saved successfully!");
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { toast.error("Error saving: " + getFriendlyError(e)); }
    setSaving(false);
  };

  return (
    <div>
      <div style={{ marginBottom:22 }}>
        <h2 style={{ fontSize:21, fontWeight:800, color:T.text, marginBottom:4 }}>🔐 Permission Editor</h2>
        <p style={{ color:T.muted, fontSize:13 }}>Super Admin only — customize what each role can access</p>
      </div>
      <div style={{ background:T.rose+"10", border:`1px solid ${T.rose}30`, borderRadius:9, padding:"10px 14px", marginBottom:18, fontSize:13, color:T.rose }}>
        ⚠️ Changes take effect after saving. Locked items (grey) cannot be removed.
      </div>
      <div style={{ background:T.card, borderRadius:15, border:`1px solid ${T.border}`, overflow:"hidden" }}>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ background:T.secondary }}>
                <th style={{ padding:"13px 15px", textAlign:"left", color:"rgba(255,255,255,0.65)", fontWeight:700, fontSize:11, textTransform:"uppercase", letterSpacing:0.6, width:190 }}>Page / Feature</th>
                {ROLE_KEYS.map(k => (
                  <th key={k} style={{ padding:"13px 15px", textAlign:"center", color:"white", fontWeight:700, fontSize:12 }}>
                    <div style={{ fontSize:18 }}>{roles[k]?.emoji || DEFAULT_ROLES[k]?.emoji}</div>
                    <div style={{ fontSize:10, marginTop:2, opacity:0.8 }}>{roles[k]?.label || DEFAULT_ROLES[k]?.label}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ALL_PAGES.map((page, i) => (
                <tr key={page.key} style={{ borderBottom:`1px solid ${T.border}`, background:i%2===0?"white":T.bg }}>
                  <td style={{ padding:"11px 15px", fontWeight:600, color:T.text, fontSize:13 }}>{page.label}</td>
                  {ROLE_KEYS.map(rk => {
                    const has    = roles[rk]?.pages?.includes(page.key);
                    const locked = LOCKED[rk]?.includes(page.key);
                    return (
                      <td key={rk} style={{ padding:"11px 15px", textAlign:"center" }}>
                        <button onClick={() => toggle(rk, page.key)} disabled={locked}
                          style={{ width:28, height:28, borderRadius:7, border:"none", cursor:locked?"not-allowed":"pointer", display:"inline-flex", alignItems:"center", justifyContent:"center", background:has?T.success+"20":T.rose+"10", opacity:locked?0.4:1, transition:"all 0.15s" }}>
                          {has ? <Icon name="check" size={13} color={T.success}/> : <Icon name="x" size={13} color={T.rose}/>}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding:"14px 18px", borderTop:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", background:T.bg, flexWrap:"wrap", gap:10 }}>
          <div style={{ fontSize:13, color:saved?T.success:T.muted, fontWeight:saved?700:400 }}>
            {saved ? "✅ Permissions saved!" : "Toggle permissions above then save."}
          </div>
          <div style={{ display:"flex", gap:9 }}>
            <Btn onClick={() => setRoles(DEFAULT_ROLES)} color={T.muted} outline sm>Reset to Default</Btn>
            <Btn onClick={saveRBAC} disabled={saving}><Icon name="save" size={14} color="white"/>{saving ? "Saving..." : "Save Permissions"}</Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── MANAGE USERS (Super Admin Only) ──────────────────────────────────────────
const ManageUsers = ({ roles }) => {
  const { userProfile } = useAuth();
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState({ name:"", email:"", role:"franchiseAdmin", center:"" });
  const [saving, setSaving]     = useState(false);
  // actionLoading: { [userId]: "lock"|"active"|"role"|null }
  const [actionLoading, setAL]  = useState({});

  const setALFor = (uid, val) => setAL(prev => ({ ...prev, [uid]:val }));

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "users"));
      setUsers(
        snap.docs.map(d => ({ id:d.id, ...d.data() }))
          .filter(u => u.role !== "parent")   // parents are auto-managed via student enrollment
          .sort((a,b) => (b.createdAt?.seconds||0)-(a.createdAt?.seconds||0))
      );
    } catch (e) { toast.error("Failed to load users: " + getFriendlyError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Guard AFTER all hooks
  if (userProfile?.role !== "superAdmin") return <Denied/>;

  // ── Create new user (Firestore profile only — user must log in with Firebase Auth separately)
  const createUser = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    if (!/\S+@\S+\.\S+/.test(form.email)) { toast.error("Enter a valid email"); return; }
    setSaving(true);
    try {
      // Check for duplicate
      const existing = await getDocs(query(collection(db,"users"), where("email","==",form.email.toLowerCase().trim())));
      if (!existing.empty) { toast.error("A user with this email already exists."); setSaving(false); return; }
      await addDoc(collection(db,"users"), {
        uid:       "",  // will be filled on first login
        email:     form.email.toLowerCase().trim(),
        name:      form.name.trim(),
        role:      form.role,
        center:    form.center.trim(),
        active:    true,
        locked:    false,
        createdAt: serverTimestamp(),
        createdBy: userProfile?.email,
      });
      toast.success(`User ${form.name} created. They must sign up on the portal with email: ${form.email}`);
      setForm({ name:"", email:"", role:"franchiseAdmin", center:"" });
      setModal(false);
      await load();
    } catch (e) { toast.error("Error: " + getFriendlyError(e)); }
    setSaving(false);
  };

  // ── Toggle Lock / Unlock ──────────────────────────────────────────────────
  const toggleLock = async (u) => {
    if (u.email === SUPER_ADMIN_EMAIL) { toast.warn("Cannot lock the default Super Admin account."); return; }
    if (u.email === userProfile?.email) { toast.warn("You cannot lock your own account."); return; }
    setALFor(u.id, "lock");
    try {
      const newLocked = !u.locked;
      await updateDoc(doc(db,"users",u.id), { locked:newLocked, updatedAt:serverTimestamp() });
      if (newLocked) {
        // Also lock in loginAttempts
        await setDoc(doc(db,"loginAttempts",_aKey(u.email)), {
          email: u.email, attempts: MAX_ATTEMPTS, locked: true, lockedAt: serverTimestamp()
        }, { merge:true });
        toast.warn(`Account ${u.email} locked.`);
      } else {
        await clearLoginAttempts(u.email);
        toast.success(`Account ${u.email} unlocked.`);
      }
      await load();
    } catch (e) { toast.error("Error: " + getFriendlyError(e)); }
    setALFor(u.id, null);
  };

  // ── Toggle Active / Inactive ──────────────────────────────────────────────
  const toggleActive = async (u) => {
    if (u.email === SUPER_ADMIN_EMAIL) { toast.warn("Cannot deactivate the default Super Admin account."); return; }
    if (u.email === userProfile?.email) { toast.warn("You cannot deactivate your own account."); return; }
    setALFor(u.id, "active");
    try {
      await updateDoc(doc(db,"users",u.id), { active:!u.active, updatedAt:serverTimestamp() });
      toast.success(`Account ${u.active ? "deactivated" : "activated"}.`);
      await load();
    } catch (e) { toast.error("Error: " + getFriendlyError(e)); }
    setALFor(u.id, null);
  };

  // ── Change Role ───────────────────────────────────────────────────────────
  const updRole = async (uid, role) => {
    setALFor(uid, "role");
    try {
      await updateDoc(doc(db,"users",uid), { role, updatedAt:serverTimestamp() });
      await load();
    } catch (e) { toast.error("Error: " + getFriendlyError(e)); }
    setALFor(uid, null);
  };

  const updCenter = async (uid, center) => {
    try { await updateDoc(doc(db,"users",uid), { center, updatedAt:serverTimestamp() }); }
    catch (e) { toast.error("Error: " + getFriendlyError(e)); }
  };

  const delUser = async id => {
    const u = users.find(x => x.id===id);
    if (u?.email === SUPER_ADMIN_EMAIL) { toast.warn("Cannot remove the default Super Admin."); return; }
    if (!window.confirm("Remove this user? They will lose portal access.")) return;
    try {
      await deleteDoc(doc(db,"users",id));
      await load();
      toast.success("User removed.");
    } catch (e) { toast.error("Error: " + getFriendlyError(e)); }
  };

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:22, flexWrap:"wrap", gap:12 }}>
        <div>
          <h2 style={{ fontSize:21, fontWeight:800, color:T.text, marginBottom:4 }}>Manage Users</h2>
          <p style={{ color:T.muted, fontSize:13 }}>Assign roles, lock/unlock, activate/deactivate — Super Admin only</p>
        </div>
        <Btn onClick={() => setModal(true)}><Icon name="plus" size={14} color="white"/> Add User</Btn>
      </div>

      <div style={{ background:T.rose+"10", border:`1px solid ${T.rose}30`, borderRadius:9, padding:"10px 14px", marginBottom:16, fontSize:13, color:T.rose }}>
        🔒 Only Super Admins can see this page. Lock/unlock and activate/deactivate controls are exclusive to Super Admin.
      </div>
      <div style={{ background:T.blue+"10", border:`1px solid ${T.blue}30`, borderRadius:9, padding:"10px 14px", marginBottom:16, fontSize:13, color:T.blue }}>
        👨‍👩‍👧 <strong>Child/Parent</strong> accounts are created automatically when a student is enrolled with a Parent Email. They do not appear here.
      </div>

      <div style={{ background:T.card, borderRadius:15, padding:18, border:`1px solid ${T.border}` }}>
        {loading ? <TableSkeleton rows={4} cols={7}/> : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr style={{ background:T.bg }}>
                  {["Name","Email","Role","Center","Status","Change Role","Actions"].map(h => (
                    <th key={h} style={{ padding:"8px 11px", textAlign:"left", color:T.muted, fontWeight:700, fontSize:10, textTransform:"uppercase", borderBottom:`1px solid ${T.border}`, whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isMe = u.email === userProfile?.email;
                  const isSuperAdmin = u.email === SUPER_ADMIN_EMAIL;
                  const busy = actionLoading[u.id];
                  return (
                    <tr key={u.id} style={{ borderBottom:`1px solid ${T.border}` }}
                      onMouseEnter={e => e.currentTarget.style.background=T.bg}
                      onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                      <td style={{ padding:"9px 11px", fontWeight:700, color:T.text }}>
                        {u.name||"—"}
                        {isMe && <span style={{ marginLeft:5, fontSize:10, color:T.primary }}>(you)</span>}
                        {isSuperAdmin && <span style={{ marginLeft:5, fontSize:10, color:T.warning }}>⭐ Default SA</span>}
                      </td>
                      <td style={{ padding:"9px 11px", color:T.muted, fontSize:11 }}>{u.email}</td>
                      <td style={{ padding:"9px 11px" }}><RBadge role={u.role} roles={roles}/></td>
                      <td style={{ padding:"9px 11px" }}>
                        <input defaultValue={u.center||""} onBlur={e => updCenter(u.id, e.target.value)}
                          placeholder="e.g. Powai Center"
                          style={{ padding:"4px 8px", border:`1px solid ${T.border}`, borderRadius:6, fontSize:12, width:125, outline:"none" }}/>
                      </td>
                      {/* Status badges */}
                      <td style={{ padding:"9px 11px" }}>
                        <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                          <SBadge status={u.active===false ? "Inactive" : "Active"}/>
                          {u.locked && <SBadge status="Locked"/>}
                        </div>
                      </td>
                      {/* Change Role */}
                      <td style={{ padding:"9px 11px" }}>
                        <select value={u.role||""} onChange={e => updRole(u.id, e.target.value)}
                          disabled={isSuperAdmin || busy==="role"}
                          style={{ padding:"5px 8px", border:`1px solid ${T.border}`, borderRadius:7, fontSize:12, color:T.text, background:"white", outline:"none", opacity:(isSuperAdmin||busy==="role")?0.5:1 }}>
                          {Object.entries(roles||DEFAULT_ROLES).filter(([k]) => k!=="parent").map(([k,v]) => (
                            <option key={k} value={k}>{v.emoji} {v.label}</option>
                          ))}
                        </select>
                      </td>
                      {/* Actions: Lock, Activate, Delete */}
                      <td style={{ padding:"9px 11px" }}>
                        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                          {/* Lock / Unlock */}
                          <Btn sm
                            onClick={() => toggleLock(u)}
                            disabled={isMe || isSuperAdmin || !!busy}
                            color={u.locked ? T.success : T.warning}
                            outline>
                            <Icon name={u.locked ? "unlock" : "lock"} size={11} color={u.locked ? T.success : T.warning}/>
                            {busy==="lock" ? "..." : u.locked ? "Unlock" : "Lock"}
                          </Btn>
                          {/* Activate / Deactivate */}
                          <Btn sm
                            onClick={() => toggleActive(u)}
                            disabled={isMe || isSuperAdmin || !!busy}
                            color={u.active===false ? T.success : T.rose}
                            outline>
                            {busy==="active" ? "..." : u.active===false ? "Activate" : "Deactivate"}
                          </Btn>
                          {/* Delete */}
                          {!isMe && !isSuperAdmin && (
                            <Btn sm onClick={() => delUser(u.id)} color={T.rose} outline disabled={!!busy}>
                              <Icon name="trash" size={11} color={T.rose}/>
                            </Btn>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      <Modal open={modal} onClose={() => { setModal(false); setForm({ name:"", email:"", role:"franchiseAdmin", center:"" }); }} title="➕ Add New User">
        <div style={{ background:T.blue+"10", border:`1px solid ${T.blue}30`, borderRadius:9, padding:"10px 13px", marginBottom:16, fontSize:12, color:T.blue, lineHeight:1.6 }}>
          ℹ️ This creates a portal profile. The user must sign up on the login page using this exact email address to activate their account and get a login.
        </div>
        <Inp label="Full Name" value={form.name} onChange={e => setForm({...form,name:e.target.value})} placeholder="User's full name" required/>
        <Inp label="Email Address (will be their User ID)" type="email" value={form.email} onChange={e => setForm({...form,email:e.target.value})} placeholder="user@email.com" required/>
        <Sel label="Role" value={form.role} onChange={e => setForm({...form,role:e.target.value})}
          options={Object.entries(DEFAULT_ROLES).filter(([k]) => k!=="superAdmin" && k!=="parent").map(([k,v]) => ({ value:k, label:`${v.emoji} ${v.label}` }))}/>
        <Inp label="Center Name (for Franchise Admin / Teacher)" value={form.center} onChange={e => setForm({...form,center:e.target.value})} placeholder="e.g. Powai Center"/>
        <div style={{ display:"flex", gap:9, marginTop:10 }}>
          <Btn onClick={() => { setModal(false); }} color={T.muted} outline>Cancel</Btn>
          <Btn onClick={createUser} disabled={saving}><Icon name="save" size={14} color="white"/>{saving ? "Creating..." : "Create User"}</Btn>
        </div>
      </Modal>
    </div>
  );
};


// ── CHANGE PASSWORD MODAL (First Login) ───────────────────────────────────────
const ChangePasswordModal = ({ open, onDone }) => {
  const [pw, setPw]   = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (pw.length < 6)          { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2)             { toast.error("Passwords do not match"); return; }
    if (pw === "mistermind")    { toast.error("Please choose a different password than the default"); return; }
    setSaving(true);
    try {
      if (!auth.currentUser) { toast.error("Session expired. Please log in again."); setSaving(false); return; }
      await updatePassword(auth.currentUser, pw);
      // Mark firstLogin = false in Firestore
      const snap = await getDocs(query(collection(db,"users"), where("uid","==",auth.currentUser.uid)));
      if (!snap.empty) await updateDoc(doc(db,"users",snap.docs[0].id), { firstLogin:false });
      toast.success("Password changed successfully!");
      onDone();
    } catch(e) { toast.error("Error: " + getFriendlyError(e)); }
    setSaving(false);
  };

  if (!open) return null;
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.65)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9000 }}>
      <div style={{ background:T.card, borderRadius:18, padding:32, width:"100%", maxWidth:380, boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ fontSize:32, textAlign:"center", marginBottom:8 }}>🔑</div>
        <h3 style={{ textAlign:"center", color:T.text, marginBottom:4, fontSize:18 }}>Set Your Password</h3>
        <p style={{ textAlign:"center", color:T.muted, fontSize:13, marginBottom:20 }}>
          Welcome! Please set a new password to continue. Your default password was <strong>mistermind</strong>.
        </p>
        <Inp label="New Password" type="password" value={pw}  onChange={e => setPw(e.target.value)}  placeholder="Min 6 characters" autoComplete="new-password"/>
        <Inp label="Confirm Password" type="password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder="Re-enter password" autoComplete="new-password"/>
        <Btn onClick={handleSave} disabled={saving} style={{ width:"100%", marginTop:12 }}>
          {saving ? "Saving…" : "Set Password & Continue"}
        </Btn>
      </div>
    </div>
  );
};

// ── FEE RECEIPT MODAL ─────────────────────────────────────────────────────────
const FeeReceiptModal = ({ open, onClose, student, payments, franchiseName, franchiseLogo }) => {
  if (!open || !student) return null;

  const handlePrint = () => {
    const w = window.open("","_blank","width=700,height=900");
    if (!w) { toast.warn("Popup blocked. Please allow popups for this site, then try again."); return; }
    // Sanitize all user data before injecting into HTML to prevent XSS
    const esc = (s) => String(s||"").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;").replace(/'/g,"&#39;");
    const latestPay = (payments && payments.length > 0) ? payments[0] : {};
    const totalPaid = (payments||[]).reduce((s,p) => s + (Number(p?.amount)||0), 0);
    const receiptNo = "RCP-" + esc(student.studentId) + "-" + Date.now().toString().slice(-6);
    const today = new Date().toLocaleDateString("en-IN", { day:"2-digit", month:"long", year:"numeric" });

    w.document.write(`<!DOCTYPE html><html><head><title>Fee Receipt</title>
    <style>
      body { font-family: 'Segoe UI', Arial, sans-serif; margin:0; padding:0; background:#fff; color:#1a1a2e; }
      .page { max-width:680px; margin:30px auto; padding:32px; border:2px solid #FF6B35; border-radius:12px; }
      .header { display:flex; align-items:center; justify-content:space-between; border-bottom:2px solid #FF6B35; padding-bottom:16px; margin-bottom:20px; }
      .logo-area { display:flex; align-items:center; gap:14px; }
      .logo-area img { height:60px; object-fit:contain; }
      .org-name { font-size:22px; font-weight:900; color:#FF6B35; }
      .org-sub  { font-size:12px; color:#6b7280; margin-top:2px; }
      .receipt-title { text-align:right; }
      .receipt-title h2 { margin:0; font-size:20px; color:#1a1a2e; }
      .receipt-title p  { margin:4px 0 0; font-size:12px; color:#6b7280; }
      .info-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:18px; }
      .info-box { background:#f8f7ff; border-radius:8px; padding:12px 14px; }
      .info-box label { font-size:10px; text-transform:uppercase; color:#6b7280; letter-spacing:0.6px; display:block; margin-bottom:3px; }
      .info-box strong { font-size:14px; color:#1a1a2e; }
      .amount-box { background:linear-gradient(135deg,#FF6B35,#E55A25); border-radius:10px; padding:18px 20px; color:white; text-align:center; margin-bottom:20px; }
      .amount-box .label { font-size:12px; opacity:0.85; }
      .amount-box .amount { font-size:36px; font-weight:900; margin:4px 0; }
      .amount-box .mode { font-size:12px; opacity:0.75; }
      table { width:100%; border-collapse:collapse; font-size:13px; }
      th,td { padding:9px 12px; border:1px solid #e8e4f3; text-align:left; }
      th { background:#f8f7ff; font-weight:700; color:#1a1a2e; }
      .footer { margin-top:24px; border-top:1px solid #e8e4f3; padding-top:16px; display:flex; justify-content:space-between; align-items:flex-end; font-size:12px; color:#6b7280; }
      .stamp { border:2px dashed #10b981; border-radius:8px; padding:8px 16px; color:#10b981; font-weight:700; font-size:14px; }
      @media print { body { margin:0; } .page { border:none; margin:0; padding:20px; } }
    </style></head><body>
    <div class="page">
      <div class="header">
        <div class="logo-area">
          ${franchiseLogo ? `<img src="${franchiseLogo}" alt="Logo"/>` : `<div style="width:60px;height:60px;background:#FF6B35;border-radius:10px;display:flex;align-items:center;justify-content:center;color:white;font-weight:900;font-size:22px;">M</div>`}
          <div>
            <div class="org-name">${esc(franchiseName) || "Mistermind Abacus"}</div>
            <div class="org-sub">Abacus & Vedic Maths Institute</div>
            ${student.center ? `<div class="org-sub">📍 ${esc(student.center)}</div>` : ""}
          </div>
        </div>
        <div class="receipt-title">
          <h2>FEE RECEIPT</h2>
          <p>Receipt No: <strong>${receiptNo}</strong></p>
          <p>Date: ${today}</p>
        </div>
      </div>

      <div class="amount-box">
        <div class="label">Amount Received</div>
        <div class="amount">₹${totalPaid.toLocaleString("en-IN")}</div>
        <div class="mode">${esc(latestPay.paymentMode) || "Cash"} • ${esc(latestPay.paymentDate) || today}</div>
      </div>

      <div class="info-grid">
        <div class="info-box"><label>Student Name</label><strong>${esc(student.name)}</strong></div>
        <div class="info-box"><label>Student ID</label><strong>${esc(student.studentId) || "—"}</strong></div>
        <div class="info-box"><label>Course</label><strong>${esc(student.course)}</strong></div>
        <div class="info-box"><label>Batch</label><strong>${esc(student.batch) || "—"}</strong></div>
        <div class="info-box"><label>Parent / Guardian</label><strong>${esc(student.parentName) || "—"}</strong></div>
        <div class="info-box"><label>Contact</label><strong>${esc(student.phone) || "—"}</strong></div>
      </div>

      <table>
        <thead><tr><th>#</th><th>Description</th><th>Amount (₹)</th><th>Payment Date</th><th>Mode</th></tr></thead>
        <tbody>
          ${(payments||[]).map((p,i) => `<tr>
            <td>${i+1}</td>
            <td>Course Fee — ${esc(student.course)}</td>
            <td>₹${Number(p.amount||0).toLocaleString("en-IN")}</td>
            <td>${esc(p.paymentDate)||"—"}</td>
            <td>${esc(p.paymentMode)||"Cash"}</td>
          </tr>`).join("") || `<tr><td colspan="5" style="text-align:center;color:#6b7280;">No payment records</td></tr>`}
        </tbody>
      </table>

      <div class="footer">
        <div>
          <div>Thank you for choosing ${esc(franchiseName) || "Mistermind Abacus"}!</div>
          <div style="margin-top:4px">This is a computer-generated receipt.</div>
        </div>
        <div class="stamp">✓ PAID</div>
      </div>
    </div>
    <script>window.onload=()=>{window.print();}</script>
    </body></html>`);
    w.document.close();
  };

  return (
    <Modal open={open} onClose={onClose} title={`Fee Receipt — ${student.name}`} wide>
      <div style={{ textAlign:"center", padding:"10px 0 20px" }}>
        <div style={{ fontSize:48, marginBottom:8 }}>🧾</div>
        <div style={{ fontWeight:700, fontSize:16, color:T.text, marginBottom:4 }}>{student.name}</div>
        <div style={{ color:T.muted, fontSize:13 }}>{student.course} • {student.center}</div>
        <div style={{ background:T.success+"15", border:`1px solid ${T.success}30`, borderRadius:10, padding:"14px 20px", margin:"16px 0", display:"inline-block" }}>
          <div style={{ fontSize:11, color:T.muted, marginBottom:2 }}>Total Paid</div>
          <div style={{ fontSize:28, fontWeight:900, color:T.success }}>₹{(payments||[]).reduce((s,p) => s+Number(p.amount||0),0).toLocaleString("en-IN")}</div>
        </div>
        <div style={{ fontSize:13, color:T.muted, marginBottom:20 }}>
          {payments?.length || 0} payment(s) recorded
        </div>
        <Btn onClick={handlePrint} color={T.primary}>
          <Icon name="save" size={14} color="white"/> Download / Print Receipt
        </Btn>
      </div>
    </Modal>
  );
};

// ── MEDIA MODULE ──────────────────────────────────────────────────────────────
const MediaPage = () => {
  const { userProfile } = useAuth();
  const isSA = userProfile?.role === "superAdmin";
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState({ title:"", category:"Banner", description:"" });
  const [file, setFile]         = useState(null);
  const [uploading, setUploading] = useState(false);
  const CATEGORIES = ["Banner","Pamphlet","Social Media Post","Brochure","Other"];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db,"media"));
      setItems(snap.docs.map(d => ({ id:d.id, ...d.data() })).sort((a,b) => (b.createdAt?.seconds||0)-(a.createdAt?.seconds||0)));
    } catch(e) { toast.error("Failed to load media: " + getFriendlyError(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (form.title.trim().length > 100) { toast.error("Title must be under 100 characters"); return; }
    if (/<[^>]*>/.test(form.title) || /<[^>]*>/.test(form.description)) { toast.error("Title and description cannot contain HTML tags"); return; }
    if (!file) { toast.error("Please select a file"); return; }
    const allowed = ["image/jpeg","image/png","image/gif","image/webp","application/pdf",
      "application/msword","application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (!allowed.includes(file.type)) { toast.error("Allowed: Images, PDF, Word documents only"); return; }
    if (file.size > 900 * 1024) { toast.error("File size must be under 900KB. For larger files, please compress first."); return; }
    setUploading(true);
    try {
      // Store as base64 in Firestore (up to 5MB)
      const reader = new FileReader();
      reader.onload = async ev => {
        try {
          await addDoc(collection(db,"media"), {
            ...form, fileName:file.name, fileType:file.type,
            fileData: ev.target.result,
            uploadedBy: userProfile?.email, createdAt:serverTimestamp(),
          });
          toast.success("Media uploaded successfully!");
          setModal(false); setFile(null); setForm({ title:"", category:"Banner", description:"" });
          load();
        } catch(e) { toast.error("Upload failed: " + getFriendlyError(e)); }
        setUploading(false);
      };
      reader.onerror = () => { toast.error("Could not read file"); setUploading(false); };
      reader.readAsDataURL(file);
    } catch(e) { toast.error(getFriendlyError(e)); setUploading(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm("Delete this media item?")) return;
    try { await deleteDoc(doc(db,"media",id)); toast.success("Deleted"); load(); }
    catch(e) { toast.error(getFriendlyError(e)); }
  };

  const handleDownload = item => {
    const a = document.createElement("a");
    a.href = item.fileData;
    a.download = item.fileName || item.title;
    a.click();
  };

  const catColor = c => ({ Banner:T.primary, Pamphlet:T.blue, "Social Media Post":T.teal, Brochure:T.purple, Other:T.muted }[c] || T.muted);
  const isImage  = t => t?.startsWith("image/");

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:22, flexWrap:"wrap", gap:10 }}>
        <div>
          <h2 style={{ fontSize:21, fontWeight:800, color:T.text, marginBottom:4 }}>📢 Media Library</h2>
          <div style={{ color:T.muted, fontSize:13 }}>Advertisement materials — banners, pamphlets & social media</div>
        </div>
        {isSA && <Btn onClick={() => setModal(true)}><Icon name="add" size={14} color="white"/> Upload Material</Btn>}
      </div>

      {loading ? <TableSkeleton rows={4} cols={4}/> : items.length === 0 ? (
        <div style={{ textAlign:"center", padding:60 }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🖼️</div>
          <div style={{ fontWeight:700, color:T.text, fontSize:17, marginBottom:6 }}>No media uploaded yet</div>
          <div style={{ color:T.muted, fontSize:13 }}>{isSA ? "Click 'Upload Material' to add banners, pamphlets and ads." : "No materials available."}</div>
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:16 }}>
          {items.map(item => (
            <div key={item.id} style={{ background:T.card, borderRadius:14, border:`1px solid ${T.border}`, overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              {isImage(item.fileType) ? (
                <img src={item.fileData} alt={item.title} style={{ width:"100%", height:140, objectFit:"cover" }}/>
              ) : (
                <div style={{ width:"100%", height:140, background:catColor(item.category)+"15", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:6 }}>
                  <div style={{ fontSize:40 }}>{item.fileType==="application/pdf" ? "📄" : "📝"}</div>
                  <div style={{ fontSize:11, color:T.muted }}>{item.fileName}</div>
                </div>
              )}
              <div style={{ padding:"12px 14px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:6 }}>
                  <Badge color={catColor(item.category)}>{item.category}</Badge>
                </div>
                <div style={{ fontWeight:700, color:T.text, fontSize:14, marginBottom:3 }}>{item.title}</div>
                {item.description && <div style={{ color:T.muted, fontSize:12, marginBottom:8, lineHeight:1.4 }}>{item.description}</div>}
                <div style={{ fontSize:10, color:T.muted, marginBottom:10 }}>By {item.uploadedBy}</div>
                <div style={{ display:"flex", gap:7 }}>
                  <Btn sm onClick={() => handleDownload(item)} color={T.blue}><Icon name="save" size={11} color="white"/> Download</Btn>
                  {isSA && <Btn sm onClick={() => handleDelete(item.id)} color={T.rose}><Icon name="x" size={11} color="white"/> Delete</Btn>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => { setModal(false); setFile(null); }} title="Upload Media Material" wide>
        <Inp label="Title" value={form.title} onChange={e => setForm({...form,title:e.target.value})} placeholder="e.g. Summer Banner 2025" required/>
        <Sel label="Category" value={form.category} onChange={e => setForm({...form,category:e.target.value})} options={CATEGORIES}/>
        <Inp label="Description (Optional)" value={form.description} onChange={e => setForm({...form,description:e.target.value})} placeholder="Brief description"/>
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:12, fontWeight:600, color:T.text, marginBottom:6 }}>File (Image / PDF / Word — max 900KB)</div>
          <input type="file" accept="image/*,.pdf,.doc,.docx" onChange={e => setFile(e.target.files?.[0]||null)}
            style={{ fontSize:13, color:T.text }}/>
          {file && <div style={{ fontSize:12, color:T.success, marginTop:5 }}>✓ {file.name} ({(file.size/1024).toFixed(1)} KB)</div>}
        </div>
        <div style={{ display:"flex", gap:9, marginTop:6 }}>
          <Btn onClick={() => { setModal(false); setFile(null); }} color={T.muted} outline>Cancel</Btn>
          <Btn onClick={handleUpload} disabled={uploading}><Icon name="save" size={14} color="white"/>{uploading ? "Uploading…" : "Upload"}</Btn>
        </div>
      </Modal>
    </div>
  );
};

// ── PRACTICE TEST PAGE (Child/Parent) ─────────────────────────────────────────
const PracticeTestPage = () => {
  const { userProfile } = useAuth();
  const [student, setStudent]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [phase, setPhase]       = useState("select"); // select | test | result
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers]   = useState([]);
  const [current, setCurrent]   = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timerOn, setTimerOn]   = useState(false);
  const [testType, setTestType] = useState("abacus");
  const [difficulty, setDiff]   = useState("easy");
  const [pastResults, setPastResults] = useState([]);
  const TIMER_SECS = 120;

  useEffect(() => {
    const go = async () => {
      try {
        const snap = await getDocs(query(collection(db,"students"), where("parentEmail","==",userProfile?.email?.toLowerCase()||"")));
        if (!snap.empty) {
          const st = { id:snap.docs[0].id, ...snap.docs[0].data() };
          setStudent(st);
          // Load past practice results
          try {
            const rSnap = await getDocs(query(collection(db,"practiceResults"), where("studentId","==",st.id)));
            const results = rSnap.docs.map(d => d.data()).sort((a,b) => (b.takenAt?.seconds||0)-(a.takenAt?.seconds||0));
            setPastResults(results);
          } catch { /* practiceResults collection may not exist yet */ }
        }
      } catch(e) { toast.error("An error occurred: " + getFriendlyError(e)); }
      setLoading(false);
    };
    go();
  }, [userProfile]);

  // Detect test type from course
  useEffect(() => {
    if (student?.course) {
      setTestType(student.course.toLowerCase().includes("vedic") ? "vedic" : "abacus");
      const lvl = parseInt(student.course.replace(/\D/g,"")) || 1;
      setDiff(lvl <= 3 ? "easy" : lvl <= 6 ? "medium" : "hard");
    }
  }, [student]);

  useEffect(() => {
    if (!timerOn) return;
    if (timeLeft <= 0) { setTimerOn(false); setPhase("result"); return; }
    const t = setTimeout(() => setTimeLeft(p => p-1), 1000);
    return () => clearTimeout(t);
  }, [timerOn, timeLeft]);

  const finishTest = async () => {
    setTimerOn(false);
    setPhase("result");
    // Save result to Firestore
    if (student?.id) {
      try {
        const sc = questions.reduce((s,q,i) => s+(answers[i]!==""&&answers[i]!==undefined&&Number(answers[i])===q.ans?1:0), 0);
        const pct = questions.length>0 ? Math.round((sc/questions.length)*100) : 0;
        await addDoc(collection(db,"practiceResults"), {
          studentId: student.id, studentName: student.name,
          parentEmail: userProfile?.email?.toLowerCase()||"",
          testType, difficulty, score:sc, total:questions.length, pct,
          takenAt: serverTimestamp(),
        });
        // Refresh past results
        const rSnap = await getDocs(query(collection(db,"practiceResults"), where("studentId","==",student.id)));
        setPastResults(rSnap.docs.map(d => d.data()).sort((a,b) => (b.takenAt?.seconds||0)-(a.takenAt?.seconds||0)));
      } catch { /* non-critical — result display still works */ }
    }
  };

  const rnd = (min,max) => Math.floor(Math.random()*(max-min+1))+min;

  const genAbacus = diff => {
    const [lo,hi] = diff==="easy"?[1,20]:diff==="medium"?[10,99]:[100,999];
    return Array.from({length:10}, (_,i) => {
      const ops = diff==="easy"?["+","-"]:diff==="medium"?["+","-","×"]:["+","-","×","÷"];
      const op  = ops[rnd(0,ops.length-1)];
      let a = rnd(lo,hi), b = rnd(1,diff==="easy"?10:diff==="medium"?50:100), ans;
      if (op==="+" )             { ans = a+b; }
      else if (op==="-" )        { b = rnd(1,a); ans = a-b; }
      else if (op==="×")         { b = rnd(2,diff==="easy"?5:12); ans = a*b; }
      else { b = rnd(2,diff==="easy"?5:10); a = b*rnd(2,10); ans = a/b; }
      return { q:`${a} ${op} ${b} = ?`, ans, id:i };
    });
  };

  const genVedic = diff => {
    return Array.from({length:10}, (_,i) => {
      const type = rnd(0,3);
      let q, ans, a, b;
      if (type===0) { // Squaring numbers ending in 5
        a = (rnd(1,diff==="easy"?3:diff==="medium"?7:9)*10)+5; ans=a*a;
        q=`${a}² = ?`;
      } else if (type===1) { // Multiply near 100
        a = rnd(diff==="easy"?91:85,99); b = rnd(diff==="easy"?91:85,99); ans=a*b;
        q=`${a} × ${b} = ?`;
      } else if (type===2) { // Sum of series
        const n = rnd(5,diff==="easy"?10:20); ans = n*(n+1)/2;
        q=`1+2+3+…+${n} = ?`;
      } else { // Subtraction from 1000
        a = rnd(diff==="easy"?100:1,999); ans=1000-a;
        q=`1000 − ${a} = ?`;
      }
      return { q, ans, id:i };
    });
  };

  const startTest = () => {
    const qs = testType==="vedic" ? genVedic(difficulty) : genAbacus(difficulty);
    setQuestions(qs); setAnswers(Array(qs.length).fill("")); setCurrent(0);
    setTimeLeft(TIMER_SECS); setTimerOn(true); setPhase("test");
  };


  const handleAnswer = val => {
    setAnswers(prev => { const a=[...prev]; a[current]=val; return a; });
  };

  const handleNext = () => {
    if (current < questions.length-1) setCurrent(p=>p+1);
    else finishTest();
  };

  const score = questions.reduce((s,q,i) => s+(answers[i]!==""&&answers[i]!==undefined&&Number(answers[i])===q.ans?1:0), 0);
  const pct   = questions.length>0 ? Math.round((score/questions.length)*100) : 0;
  const mins  = String(Math.floor(timeLeft/60)).padStart(2,"0");
  const secs  = String(timeLeft%60).padStart(2,"0");

  const CHARS = ["🐯","🦁","🐸","🦊","🐧","🦄","🐼","🤖"];
  const char  = CHARS[Math.floor(Math.random()*CHARS.length)];

  if (loading) return <div style={{ textAlign:"center", padding:60, color:T.muted }}>Loading…</div>;
  if (!student) return (
    <div style={{ textAlign:"center", padding:60 }}>
      <div style={{ fontSize:60, marginBottom:12 }}>🎯</div>
      <div style={{ fontWeight:800, fontSize:18, color:T.text }}>No student linked</div>
      <div style={{ color:T.muted, fontSize:13, marginTop:6 }}>Contact your admin to link your child.</div>
    </div>
  );

  // Select screen
  if (phase==="select") return (
    <div style={{ maxWidth:500, margin:"0 auto", textAlign:"center", padding:"20px 0" }}>
      <div style={{ fontSize:70, marginBottom:8 }}>🎮</div>
      <h2 style={{ fontSize:24, fontWeight:900, color:T.text, marginBottom:4 }}>Practice Time!</h2>
      <p style={{ color:T.muted, marginBottom:24, fontSize:14 }}>Hi <strong>{student.name}</strong>! Let's practice <strong>{student.course}</strong>.</p>
      <div style={{ background:`linear-gradient(135deg,${T.primary}15,${T.accent}15)`, borderRadius:16, padding:24, marginBottom:20, border:`2px dashed ${T.primary}50` }}>
        <div style={{ fontSize:50, marginBottom:8 }}>{testType==="vedic"?"🧮":"🔢"}</div>
        <div style={{ fontWeight:700, fontSize:16, color:T.text, marginBottom:4 }}>
          {testType==="vedic" ? "Vedic Maths" : "Abacus"} — {difficulty.charAt(0).toUpperCase()+difficulty.slice(1)} Level
        </div>
        <div style={{ color:T.muted, fontSize:13, marginBottom:16 }}>10 questions • {TIMER_SECS} seconds timer</div>
        <div style={{ display:"flex", gap:8, justifyContent:"center", flexWrap:"wrap" }}>
          {["easy","medium","hard"].map(d => (
            <button key={d} onClick={() => setDiff(d)}
              style={{ padding:"7px 18px", borderRadius:20, border:`2px solid ${difficulty===d?T.primary:T.border}`, background:difficulty===d?T.primary:"white", color:difficulty===d?"white":T.text, fontWeight:700, fontSize:12, cursor:"pointer" }}>
              {d==="easy"?"🌟 Easy":d==="medium"?"⭐⭐ Medium":"🌠 Hard"}
            </button>
          ))}
        </div>
      </div>
      <Btn onClick={startTest} style={{ padding:"14px 40px", fontSize:16 }}>
        🚀 Start Test!
      </Btn>

      {/* Past Results */}
      {pastResults.length > 0 && (
        <div style={{ marginTop:28, textAlign:"left" }}>
          <div style={{ fontWeight:800, fontSize:14, color:T.text, marginBottom:12 }}>📊 Past Performance</div>
          <div style={{ background:T.card, borderRadius:14, border:`1px solid ${T.border}`, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr style={{ background:T.bg }}>
                  {["Date","Type","Level","Score","Result"].map(h => (
                    <th key={h} style={{ padding:"8px 12px", textAlign:"left", color:T.muted, fontWeight:700, fontSize:10, textTransform:"uppercase", borderBottom:`1px solid ${T.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pastResults.slice(0,10).map((r,i) => {
                  const date = r.takenAt?.seconds ? new Date(r.takenAt.seconds*1000).toLocaleDateString("en-IN") : "—";
                  return (
                    <tr key={i} style={{ borderBottom:`1px solid ${T.border}`, background:i%2===0?"white":T.bg }}>
                      <td style={{ padding:"8px 12px", color:T.muted }}>{date}</td>
                      <td style={{ padding:"8px 12px" }}><Badge color={r.testType==="vedic"?T.teal:T.primary}>{r.testType==="vedic"?"Vedic":"Abacus"}</Badge></td>
                      <td style={{ padding:"8px 12px", color:T.text, textTransform:"capitalize" }}>{r.difficulty}</td>
                      <td style={{ padding:"8px 12px", fontWeight:700, color:T.text }}>{r.score}/{r.total}</td>
                      <td style={{ padding:"8px 12px" }}>
                        <span style={{ fontWeight:700, color:r.pct>=80?T.success:r.pct>=50?T.warning:T.rose }}>{r.pct}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  // Test screen
  if (phase==="test") {
    const q = questions[current];
    return (
      <div style={{ maxWidth:520, margin:"0 auto" }}>
        {/* Timer bar */}
        <div style={{ background:T.card, borderRadius:12, padding:"12px 18px", marginBottom:16, border:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontWeight:700, color:T.text }}>Question {current+1} / {questions.length}</div>
          <div style={{ fontWeight:900, fontSize:18, color:timeLeft<20?T.danger:T.success }}>⏱ {mins}:{secs}</div>
        </div>
        <div style={{ width:"100%", height:6, background:T.border, borderRadius:3, marginBottom:20, overflow:"hidden" }}>
          <div style={{ width:`${(timeLeft/TIMER_SECS)*100}%`, height:"100%", background:timeLeft<20?T.danger:T.primary, transition:"width 1s linear", borderRadius:3 }}/>
        </div>
        {/* Question card */}
        <div style={{ background:`linear-gradient(135deg,${T.primary},${T.primaryDark})`, borderRadius:20, padding:32, textAlign:"center", marginBottom:20, color:"white", boxShadow:`0 8px 30px ${T.primary}50` }}>
          <div style={{ fontSize:50, marginBottom:10 }}>{char}</div>
          <div style={{ fontSize:28, fontWeight:900, letterSpacing:1 }}>{q.q}</div>
        </div>
        {/* Answer input */}
        <input type="number" value={answers[current]} onChange={e => handleAnswer(e.target.value)}
          placeholder="Type your answer here…"
          style={{ width:"100%", padding:"16px 20px", fontSize:20, fontWeight:700, borderRadius:14, border:`2px solid ${T.primary}`, outline:"none", textAlign:"center", boxSizing:"border-box", color:T.text, marginBottom:16 }}
          onKeyDown={e => e.key==="Enter" && handleNext()}
          autoFocus/>
        <div style={{ display:"flex", gap:10 }}>
          {current>0 && <Btn onClick={() => setCurrent(p=>p-1)} color={T.muted} outline style={{ flex:1 }}>← Back</Btn>}
          <Btn onClick={handleNext} style={{ flex:2, padding:"13px 0", fontSize:15 }}>
            {current===questions.length-1 ? "✅ Finish!" : "Next →"}
          </Btn>
        </div>
        <div style={{ textAlign:"center", marginTop:12 }}>
          <button onClick={finishTest} style={{ background:"none", border:"none", color:T.muted, fontSize:12, cursor:"pointer", textDecoration:"underline" }}>Submit early</button>
        </div>
      </div>
    );
  }

  // Result screen
  return (
    <div style={{ maxWidth:500, margin:"0 auto", textAlign:"center" }}>
      <div style={{ fontSize:72, marginBottom:8 }}>{pct>=80?"🏆":pct>=50?"🌟":"💪"}</div>
      <h2 style={{ fontSize:26, fontWeight:900, color:T.text, marginBottom:4 }}>
        {pct>=80?"Excellent!":pct>=50?"Good Job!":"Keep Practising!"}
      </h2>
      <div style={{ color:T.muted, marginBottom:24, fontSize:14 }}>Here's how {student.name} did:</div>
      <div style={{ background:`linear-gradient(135deg,${pct>=80?T.success:pct>=50?T.warning:T.rose},${pct>=80?"#0D9268":pct>=50?"#D97706":T.danger})`, borderRadius:20, padding:28, color:"white", marginBottom:20 }}>
        <div style={{ fontSize:56, fontWeight:900 }}>{pct}%</div>
        <div style={{ fontSize:14, opacity:0.85 }}>{score} correct out of {questions.length}</div>
      </div>
      <div style={{ background:T.card, borderRadius:14, border:`1px solid ${T.border}`, overflow:"hidden", marginBottom:20 }}>
        {questions.map((q,i) => {
          const correct = answers[i]!=="" && answers[i]!==undefined && Number(answers[i])===q.ans;
          return (
            <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 16px", borderBottom:i<questions.length-1?`1px solid ${T.border}`:"none", background:i%2===0?"white":T.bg }}>
              <div style={{ fontSize:13, color:T.text }}>{q.q}</div>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <span style={{ fontSize:12, color:T.muted }}>Your: {answers[i]||"—"}</span>
                <span style={{ fontSize:12, fontWeight:700, color:correct?T.success:T.rose }}>{correct?"✓":` ✗ ${q.ans}`}</span>
              </div>
            </div>
          );
        })}
      </div>
      <Btn onClick={() => { setPhase("select"); setQuestions([]); setAnswers([]); setCurrent(0); }} style={{ padding:"13px 32px", fontSize:15 }}>
        🔄 Try Again!
      </Btn>
    </div>
  );
};

// ── MY CHILD (Parent Portal) ──────────────────────────────────────────────────
const MyChild = () => {
  const { userProfile } = useAuth();
  const [students, setStudents] = useState([]);
  const [att, setAtt]   = useState([]);
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const go = async () => {
      try {
        const snap = await getDocs(query(collection(db,"students"), where("parentEmail","==",userProfile?.email?.toLowerCase()||"")));
        const data = snap.docs.map(d => ({ id:d.id, ...d.data() }));
        setStudents(data);
        if (data.length > 0) {
          const [aSnap, fSnap] = await Promise.all([
            getDocs(query(collection(db,"attendance"), where("studentId","==",data[0].id))),
            getDocs(query(collection(db,"fees"), where("studentId","==",data[0].id))),
          ]);
          setAtt(aSnap.docs.map(d => d.data()).sort((a,b) => new Date(b.date)-new Date(a.date)));
          setFees(fSnap.docs.map(d => d.data()).sort((a,b) => new Date(b.paymentDate)-new Date(a.paymentDate)));
        }
      } catch (e) { toast.error("Failed to load your child's data: " + getFriendlyError(e)); }
      setLoading(false);
    };
    go();
  }, [userProfile]);

  if (loading) return <div style={{ textAlign:"center", padding:60, color:T.muted }}>Loading your child's data...</div>;
  if (students.length === 0) return (
    <div style={{ textAlign:"center", padding:60 }}>
      <div style={{ fontSize:48, marginBottom:14 }}>👨‍👩‍👧</div>
      <div style={{ fontWeight:800, color:T.text, fontSize:18, marginBottom:6 }}>No student linked</div>
      <div style={{ color:T.muted, fontSize:13, maxWidth:360, margin:"0 auto", lineHeight:1.7 }}>
        Your login email <strong>{userProfile?.email}</strong> is not linked to any student yet.
        <br/>Please contact your institute admin and ask them to update the <strong>Parent Email</strong> field in your child's student record to match this email address.
      </div>
    </div>
  );

  const s = students[0];
  const present = att.filter(a => a.status==="Present").length;
  const pct = att.length > 0 ? Math.round((present/att.length)*100) : 0;

  return (
    <div>
      <div style={{ marginBottom:18 }}>
        <h2 style={{ fontSize:21, fontWeight:800, color:T.text, marginBottom:4 }}>My Child's Dashboard</h2>
        <p style={{ color:T.muted, fontSize:13 }}>Viewing data for {s.name}</p>
      </div>
      <div style={{ background:`linear-gradient(135deg,${T.primary} 0%,${T.primaryDark} 100%)`, borderRadius:15, padding:20, marginBottom:18, color:"white" }}>
        <div style={{ display:"flex", alignItems:"center", gap:15 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:18, fontWeight:900 }}>{s.name}</div>
            <div style={{ opacity:0.85, fontSize:12, marginTop:2 }}>{s.course} • {s.batch}</div>
            <div style={{ opacity:0.7, fontSize:11, marginTop:2 }}>ID: {s.studentId} • {s.center}</div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:28, fontWeight:900 }}>{pct}%</div>
            <div style={{ opacity:0.8, fontSize:11 }}>Attendance</div>
          </div>
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:11, marginBottom:18 }}>
        <Card icon="attendance" label="Present"    value={present}          color={T.success}/>
        <Card icon="x"         label="Absent"     value={att.length-present} color={T.rose}/>
        <Card icon="fees"      label="Fee Status"  value={s.fee||"Pending"} color={s.fee==="Paid"?T.success:T.rose}/>
      </div>
      {fees.length > 0 && (
        <div style={{ background:T.card, borderRadius:13, padding:18, border:`1px solid ${T.border}`, marginBottom:16 }}>
          <div style={{ fontWeight:700, fontSize:14, marginBottom:13, color:T.text }}>Payment History</div>
          {fees.map((f,i) => (
            <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:i<fees.length-1?`1px solid ${T.border}`:"none", fontSize:12 }}>
              <div>
                <div style={{ fontWeight:700 }}>₹{Number(f.amount).toLocaleString("en-IN")}</div>
                <div style={{ fontSize:10, color:T.muted }}>{f.paymentMode} • {f.paymentDate}</div>
              </div>
              <SBadge status="Paid"/>
            </div>
          ))}
        </div>
      )}
      <div style={{ background:T.card, borderRadius:13, padding:18, border:`1px solid ${T.border}` }}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:13, color:T.text }}>Recent Attendance</div>
        {att.length === 0 ? <div style={{ color:T.muted, fontSize:12 }}>No attendance records yet.</div> : (
          att.slice(0,10).map((a,i) => (
            <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:i<9?`1px solid ${T.border}`:"none" }}>
              <span style={{ fontSize:12, color:T.text }}>{a.date}</span>
              <SBadge status={a.status}/>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser]           = useState(null);
  const [userProfile, setProfile] = useState(null);
  const [page, setPage]           = useState("dashboard");
  const [authLoading, setAuthL]   = useState(true);
  const [roles, setRoles]         = useState(DEFAULT_ROLES);
  const [loginError, setLoginError] = useState("");
  const [showChangePw, setShowChangePw] = useState(false);

  useEffect(() => {
    let unsubAuth = null;

    const boot = async () => {
      // ── Phase 1: Ensure default Super Admin exists ─────────────────────────
      try {
        const saCheck = await getDocs(query(collection(db,"users"), where("email","==",SUPER_ADMIN_EMAIL)));
        if (saCheck.empty) {
          // Try creating the Firebase Auth account for super admin
          try {
            const cred = await createUserWithEmailAndPassword(auth, SUPER_ADMIN_EMAIL, SUPER_ADMIN_PWD);
            await addDoc(collection(db,"users"), {
              uid:       cred.user.uid,
              email:     SUPER_ADMIN_EMAIL,
              name:      "Kiran Gadriye",
              role:      "superAdmin",
              center:    "",
              active:    true,
              locked:    false,
              createdAt: serverTimestamp(),
              createdBy: "system",
            });
            // Sign out so the app doesn't auto-log in
            await signOut(auth);
          } catch (authErr) {
            // Auth account may already exist without a Firestore profile
            // That will be auto-fixed on first login (see onAuthStateChanged below)
            if (!authErr.code?.includes("email-already-in-use")) {
              /* super admin init auth error — account may already exist */
            }
          }
        }
      } catch (initErr) {
        /* super admin init error — non-critical, will retry on next load */
      }

      // ── Phase 2: Auth listener ─────────────────────────────────────────────
      unsubAuth = onAuthStateChanged(auth, async (u) => {
        if (u) {
          setUser(u);
          try {
            const snap = await getDocs(query(collection(db,"users"), where("uid","==",u.uid)));

            let profileData = null;

            if (!snap.empty) {
              profileData = { id:snap.docs[0].id, ...snap.docs[0].data() };
            } else if (u.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase()) {
              // Auth exists but no Firestore profile — auto-create for super admin
              const ref = await addDoc(collection(db,"users"), {
                uid: u.uid, email: SUPER_ADMIN_EMAIL, name: "Kiran Gadriye",
                role: "superAdmin", center: "", active: true, locked: false,
                createdAt: serverTimestamp(), createdBy: "system",
              });
              profileData = { id:ref.id, uid:u.uid, email:SUPER_ADMIN_EMAIL, name:"Kiran Gadriye", role:"superAdmin", center:"", active:true, locked:false };
            } else {
              // Check if a profile exists by email (uid may not be set yet)
              const emailSnap = await getDocs(query(collection(db,"users"), where("email","==",u.email?.toLowerCase())));
              if (!emailSnap.empty) {
                // Update the uid field and use this profile
                profileData = { id:emailSnap.docs[0].id, ...emailSnap.docs[0].data() };
                await updateDoc(doc(db,"users",emailSnap.docs[0].id), { uid: u.uid });
              }
            }

            if (!profileData) {
              // No profile found at all — sign out
              await signOut(auth);
              setLoginError("No portal profile found for this account. Contact your Super Admin.");
              setUser(null); setProfile(null); setAuthL(false);
              return;
            }

            // Check deactivated
            if (profileData.active === false) {
              await signOut(auth);
              setLoginError("Your account has been deactivated. Contact your Super Admin.");
              setUser(null); setProfile(null); setAuthL(false);
              return;
            }
            // Check locked
            if (profileData.locked === true) {
              await signOut(auth);
              setLoginError(`Your account is locked after too many failed login attempts. Contact your Super Admin or use Forgot Password.`);
              setUser(null); setProfile(null); setAuthL(false);
              return;
            }

            setProfile(profileData);

            // Set landing page based on role
            if (profileData.role === "parent")  setPage("myChild");
            else if (profileData.role === "teacher") setPage("dashboard");
            else setPage("dashboard");

            // Trigger password change for first-time logins
            if (profileData.firstLogin === true) setShowChangePw(true);

            // Load custom RBAC
            try {
              const rbacDoc = await getDoc(doc(db,"config","rbac"));
              if (rbacDoc.exists()) {
                const saved = rbacDoc.data().roles;
                if (saved) {
                  const merged = {};
                  Object.keys(DEFAULT_ROLES).forEach(k => {
                    merged[k] = { ...DEFAULT_ROLES[k], pages:saved[k]?.pages || DEFAULT_ROLES[k].pages };
                  });
                  setRoles(merged);
                }
              }
            } catch (rbacErr) { /* RBAC load silently falls back to defaults */ }

          } catch (e) {
            console.warn("Auth state error:", e?.code || e?.message);
            toast.error("Sign-in error: " + getFriendlyError(e));
            await signOut(auth);
            setUser(null); setProfile(null);
          }
        } else {
          setUser(null);
          setProfile(null);
        }
        setAuthL(false);
      });
    };

    boot();
    return () => { if (unsubAuth) unsubAuth(); };
  }, []);

  const signOutUser = async () => {
    try {
      clearAllCache();
      await signOut(auth);
      setPage("dashboard");
      setProfile(null);
    } catch (e) { toast.error("Sign out error: " + getFriendlyError(e)); }
  };

  // ── Loading screen ────────────────────────────────────────────────────────
  if (authLoading) return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:T.bg, fontFamily:"'Segoe UI',system-ui,sans-serif", gap:14 }}>
      <div style={{ background:"white", borderRadius:14, padding:"8px 16px", width:"auto", maxWidth:340, boxShadow:"0 4px 20px rgba(0,0,0,0.08)", display:"inline-flex", alignItems:"center", justifyContent:"center" }}>
        <Logo h={50}/>
      </div>
      <div style={{ color:T.muted, fontSize:14 }}>Loading portal...</div>
    </div>
  );

  // ── Login screen ──────────────────────────────────────────────────────────
  if (!user || !userProfile) return (
    <>
      <ToastProvider/>
      <LoginPage loginError={loginError} onClearError={() => setLoginError("")}/>
    </>
  );

  // ── Authenticated app ─────────────────────────────────────────────────────
  const role = userProfile?.role;
  const ri   = (role && (roles[role] || DEFAULT_ROLES[role])) || DEFAULT_ROLES.franchiseAdmin;
  const canSee = (p) => {
    try {
      return roles?.[role]?.pages?.includes(p) || DEFAULT_ROLES?.[role]?.pages?.includes(p) || false;
    } catch { return false; }
  };

  const ALL_NAV = [
    { key:"dashboard",      label:"Dashboard",       icon:"dashboard"   },
    { key:"students",       label:"Students",        icon:"students"    },
    { key:"myStudents",     label:"My Students",     icon:"myStudents"  },
    { key:"attendance",     label:"Attendance",      icon:"attendance"  },
    { key:"fees",           label:"Fees & Payments", icon:"fees"        },
    { key:"manageTeachers", label:"Manage Teachers", icon:"teachers"    },
    { key:"franchise",      label:"Franchises",      icon:"franchise"   },
    { key:"media",          label:"Media Library",   icon:"leads"       },
    { key:"reports",        label:"Reports",         icon:"reports"     },
    { key:"manageUsers",    label:"Manage Users",    icon:"manageUsers" },
    { key:"rbacEditor",     label:"Permissions",     icon:"rbacEditor"  },
    { key:"myChild",        label:"My Child",        icon:"myChild"     },
    { key:"practice",       label:"Practice Test",   icon:"students"    },
  ];
  const navItems = ALL_NAV.filter(n => canSee(n.key));

  const renderPage = () => {
    if (!canSee(page)) return <Denied/>;
    switch (page) {
      case "dashboard":      return <Dashboard roles={roles}/>;
      case "students":
      case "myStudents":     return <StudentsPage roles={roles}/>;
      case "attendance":     return <AttendancePage/>;
      case "fees":           return <FeesPage/>;
      case "manageTeachers": return <ManageTeachers/>;
      case "franchise":      return <ComingSoon title="Franchise Management" icon="🏫" description="Manage franchise centers, territories, royalties and franchise-level reporting. This feature is under development and will be available in the next phase."/>;
      case "reports":        return <ComingSoon title="Reports & Analytics" icon="📊" description="Detailed reports on student performance, fee collection trends, attendance analytics, and franchise-level dashboards. Coming soon in the next phase."/>;
      case "manageUsers":    return <ManageUsers roles={roles}/>;
      case "rbacEditor":     return <RBACEditor roles={roles} setRoles={setRoles}/>;
      case "myChild":        return <MyChild/>;
      case "media":          return <MediaPage/>;
      case "practice":       return <PracticeTestPage/>;
      default:               return role === "parent" ? <MyChild/> : <Dashboard roles={roles}/>;
    }
  };

  return (
    <AuthCtx.Provider value={{ user, userProfile, role }}>
      <ChangePasswordModal open={showChangePw} onDone={() => { setShowChangePw(false); setProfile(p => ({...p, firstLogin:false})); }}/>
      <ToastProvider/>
      <div style={{ display:"flex", minHeight:"100vh", background:T.bg, fontFamily:"'Segoe UI',system-ui,sans-serif" }}>

        {/* ── Sidebar ── */}
        <div style={{ width:228, background:T.secondary, display:"flex", flexDirection:"column", flexShrink:0, position:"sticky", top:0, height:"100vh", overflowY:"auto" }}>
          {/* Logo */}
          <div style={{ padding:"12px 14px", borderBottom:"1px solid rgba(255,255,255,0.08)", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div style={{ background:"rgba(255,255,255,0.96)", borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", width:"100%", overflow:"hidden" }}>
              <Logo h={32}/>
            </div>
          </div>

          {/* Role card */}
          <div style={{ padding:"10px 10px 0" }}>
            <div style={{ background:ri.color+"22", border:`1px solid ${ri.color}44`, borderRadius:10, padding:"9px 11px", marginBottom:11 }}>
              <div style={{ color:"white", fontWeight:700, fontSize:12, marginBottom:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {role==="parent" ? <StudentNameBadge email={userProfile?.email}/> : (userProfile?.name || user.email?.split("@")[0])}
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                <span style={{ fontSize:14 }}>{ri.emoji}</span>
                <span style={{ color:ri.color, fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>{ri.label}</span>
              </div>
              {role!=="parent" && userProfile?.center && <div style={{ color:"rgba(255,255,255,0.42)", fontSize:9, marginTop:2 }}>📍 {userProfile.center}</div>}
            </div>
          </div>

          {/* Nav */}
          <div style={{ padding:"0 10px", flex:1 }}>
            {navItems.map(item => (
              <button key={item.key} onClick={() => setPage(item.key)}
                style={{ width:"100%", background:page===item.key?ri.color+"22":"transparent", border:page===item.key?`1px solid ${ri.color}44`:"1px solid transparent", borderRadius:8, padding:"8px 10px", cursor:"pointer", display:"flex", alignItems:"center", gap:8, marginBottom:2, color:page===item.key?"white":"rgba(255,255,255,0.52)", fontWeight:page===item.key?700:500, fontSize:12, textAlign:"left", transition:"all 0.13s" }}
                onMouseEnter={e => { if(page!==item.key) e.currentTarget.style.background="rgba(255,255,255,0.06)"; }}
                onMouseLeave={e => { if(page!==item.key) e.currentTarget.style.background="transparent"; }}>
                <Icon name={item.icon} size={14} color={page===item.key?ri.color:"rgba(255,255,255,0.38)"}/>
                {item.label}
              </button>
            ))}
          </div>

          {/* Sign out */}
          <div style={{ padding:"10px", borderTop:"1px solid rgba(255,255,255,0.08)" }}>
            <button onClick={signOutUser}
              style={{ width:"100%", background:"transparent", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"8px 10px", cursor:"pointer", display:"flex", alignItems:"center", gap:8, color:"rgba(255,255,255,0.48)", fontSize:12, transition:"all 0.13s" }}
              onMouseEnter={e => { e.currentTarget.style.background="rgba(239,68,68,0.15)"; e.currentTarget.style.color=T.rose; }}
              onMouseLeave={e => { e.currentTarget.style.background="transparent"; e.currentTarget.style.color="rgba(255,255,255,0.48)"; }}>
              <Icon name="logout" size={14} color="currentColor"/> Sign Out
            </button>
          </div>
        </div>

        {/* ── Main Content ── */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0, overflow:"hidden" }}>
          {/* Top Header */}
          <div style={{ background:T.card, borderBottom:`1px solid ${T.border}`, padding:"13px 22px", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:10 }}>
            <div style={{ fontSize:14, fontWeight:700, color:T.text }}>
              {navItems.find(n => n.key===page)?.label || "Dashboard"}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <RBadge role={role} roles={roles}/>
              <div style={{ background:T.bg, border:`1px solid ${T.border}`, borderRadius:10, padding:"6px 12px", fontSize:12, color:T.muted, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {user.email}
              </div>
            </div>
          </div>

          {/* Page Content */}
          <div style={{ flex:1, padding:"22px", overflow:"auto" }}>
            {renderPage()}
          </div>
        </div>
      </div>
    </AuthCtx.Provider>
  );
}
